import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useSpring } from 'framer-motion';
import {
  ShieldCheck, KeyRound, FileSearch, Users2, Lock, Gauge, Globe2,
  Wallet, ClipboardCheck, UserCog, Fingerprint, Database,
  Terminal, Braces, Webhook, FlaskConical, Megaphone, Scale, Briefcase, Layers,
  Brain, Eye, Film, Sparkles, Cpu, Zap, Wand2, MousePointer2, MessageCircle,
  ScanSearch, Move3d, Infinity as InfinityIcon, Music2, Code2,
} from 'lucide-react';
import { NavBar } from './ui/TubelightNavbar.jsx';
import { Logo } from './Logo';
import { Footer } from './ui/Footer';
import LoginModal from './auth/LoginModal';
import { CinematicBackground } from './ui/CinematicBackground';

const SP = {
  bg:'transparent', canvas:'rgba(0,0,0,0.4)', canvasCool:'rgba(0,0,0,0.6)',
  ink:'#FFFFFF', inkSoft:'rgba(255,255,255,0.9)', muted:'rgba(255,255,255,0.7)', subtle:'rgba(255,255,255,0.4)',
  line:'rgba(255,255,255,0.1)', lineSoft:'rgba(255,255,255,0.05)',
  accentA:'#7C3AED', accentB:'#A78BFA', accentC:'#C4B5FD',
  accentGrad:'linear-gradient(135deg,#7C3AED 0%,#A78BFA 100%)',
  accentTint:'rgba(124,58,237,0.1)', accentTintB:'rgba(124,58,237,0.2)', accentDeep:'#C4B5FD',
  signal:'#10B981',
};
const SERIF = '"Nasalization","Instrument Serif",Georgia,serif';
const MONO  = '"JetBrains Mono",ui-monospace,Menlo,monospace';
const MAIL  = 'mailto:enterprise@daggpt.network';

function useBp(){
  const read=()=>{ if(typeof window==='undefined') return {m:false,t:false};
    const w=window.innerWidth; return {m:w<640,t:w>=640&&w<1024}; };
  const [b,s]=useState(read);
  useEffect(()=>{ const h=()=>s(read()); window.addEventListener('resize',h);
    return ()=>window.removeEventListener('resize',h); },[]);
  return b;
}

const CAPS = [
  {num:'01',status:'live',   tag:'IDENTITY',  title:'Enterprise SSO',          std:'SAML 2.0 · OIDC · SCIM 2.0',             scope:'Your existing identity provider',      body:'Enforce at your identity provider, we respect it. Same groups, same policies, the login your team already uses. SCIM keeps provisioning in sync.', icon:KeyRound},
  {num:'02',status:'live',   tag:'WORKSPACES',title:'Team workspaces',         std:'Shared generations · Per-project billing', scope:'Unlimited workspaces · Roles · Visibility', body:'Seats, roles, and visibility you already understand. Shared prompt libraries, per-project cost tagging, and generation history that doesn\'t leak between matters.', icon:Users2},
  {num:'03',status:'live',   tag:'PRIVACY',   title:'Zero-training promise',   std:'Contractually · Technically · By default',  scope:'Prompts · Outputs · Metadata',         body:'We don\'t train on your content. Not your prompts, not your outputs, not the metadata around them. Written into the contract, enforced in the stack.', icon:Lock},
  {num:'04',status:'live',   tag:'BILLING',   title:'Unified billing',         std:'One invoice · One login · One admin',       scope:'All 400+ models · One vendor',         body:'Replace twelve vendor API keys with one relationship. Finance gets one PO, security gets one questionnaire, developers get one key.', icon:Wallet},
  {num:'05',status:'live',   tag:'ROSTER',    title:'400+ models, one interface', std:'OpenAI-compatible REST · Streaming',   scope:'Chat · Image · Video · Audio',         body:'GPT, Claude, Gemini, Llama, DeepSeek, Imagen, Veo, Lyria — all through one API, one UI, one bill. Switch providers without re-integrating.', icon:Layers},
  {num:'06',status:'live',   tag:'RETENTION', title:'Session-only retention',  std:'Default zero · 24h purge on delete',        scope:'No silent backups',                    body:'Prompts aren\'t retained beyond the session unless you save them. When you delete, it\'s gone from our systems within 24 hours. No "just in case" backups.', icon:Database},
  {num:'07',status:'roadmap',tag:'AUDIT',     title:'Audit logs',              std:'Exportable activity streams',              scope:'Planned: JSON export · log-store friendly', body:'Who prompted what, from where, when. Exportable to the log store your SOC already watches. On the roadmap.', icon:FileSearch},
  {num:'08',status:'roadmap',tag:'BUDGETS',   title:'Department budgets',      std:'Cost centres · Usage caps',                scope:'Planned: Per-team spend limits',       body:'Cost-centre tags, per-team caps, monthly rollups. Finance reconciliation without the spreadsheet. On the roadmap.', icon:Gauge},
  {num:'09',status:'roadmap',tag:'HOSTING',   title:'Private model hosting',   std:'Your VPC · Your KMS · Our control plane',  scope:'Planned: Single-tenant',               body:'DAGGPT running inside your AWS, GCP, or Azure account. Single-tenant deployment for regulated stacks. On the roadmap — contact sales for scoping.', icon:Globe2},
  {num:'10',status:'roadmap',tag:'COMPLIANCE',title:'SOC 2 Type II',           std:'Full audit in progress',                    scope:'Will publish the real report',         body:'We\'re going through the full audit. No "in progress" badges for marketing — we\'ll publish the report when it\'s done.', icon:ClipboardCheck},
  {num:'11',status:'roadmap',tag:'DAG AI',    title:'DAG AI models',           std:'Proprietary · Privacy-first by design',    scope:'Coming · Enterprise-first',            body:'Our own models. Trained by us, hosted by us, available to enterprise first. Capability-complete, privacy-first. Coming.', icon:Fingerprint},
  {num:'12',status:'roadmap',tag:'RETENTION+',title:'Custom retention',        std:'Per-workspace rules',                      scope:'Planned: Legal hold · Region pin',     body:'Shorter purge windows, legal hold, region pinning, per-workspace policies. Tune retention to match the matter, not the vendor default. On the roadmap.', icon:UserCog},
];

export function EnterprisePage(){
  const [login,setLogin] = useState(false);
  useEffect(()=>{ window.scrollTo(0,0); },[]);
  const nav = [
    {name:'Home',url:'/',icon:'home'},{name:'Solutions',url:'/solutions'},
    {name:'Models',url:'/models'},{name:'Enterprise',url:'/enterprise'},
  ];
  const { scrollYProgress } = useScroll();
  const sp = useSpring(scrollYProgress,{ stiffness:80, damping:28, mass:0.6 });
  return (
    <div style={{minHeight:'100vh',color:SP.ink,background:SP.bg,position:'relative',overflowX:'hidden'}}>
      <CinematicBackground />
      <div className="fixed top-4 left-4 md:top-6 md:left-8 z-50"><Logo/></div>
      <NavBar items={nav} onSignInClick={()=>setLogin(true)}/>
      <Progress progress={sp}/>
      <DataCenterHero />
      <InfrastructurePartner />
      <EnterpriseIntro />
      <PrivacyMoment/>
      <ModelRoster/>
      <DagAiModels/>
      <DataFlow/>
      <Principles/>
      <SpecSheet/>
      <PlatformAPI/>
      <VendorSprawl/>
      <UseCases/>
      <Switching/>
      <Topology/>
      <Pricing/>
      <Manifesto/>
      <Onboarding/>
      <Footer/>
      <LoginModal isOpen={login} onClose={()=>setLogin(false)}/>
    </div>
  );
}

/* ═══ PRIMITIVES ═══ */

function Progress({progress}){
  const sx = useTransform(progress,[0,1],[0,1]);
  return <motion.div style={{position:'fixed',top:0,left:0,right:0,height:'2px',background:SP.accentGrad,transformOrigin:'left',scaleX:sx,zIndex:200}}/>;
}

function SectionHead({eyebrow,title,em,tail='.',tagline,icon:Icon}){
  return (
    <div style={{display:'flex',alignItems:'flex-end',justifyContent:'space-between',flexWrap:'wrap',gap:'32px',marginBottom:'56px',paddingBottom:'24px',borderBottom:`1px solid ${SP.line}`}}>
      <div>
        <div style={{display:'inline-flex',alignItems:'center',gap:'8px',marginBottom:'16px'}}>
          {Icon ? <Icon size={13} color={SP.accentA} strokeWidth={2}/> : <span style={{width:'6px',height:'6px',borderRadius:'50%',background:SP.accentGrad}}/>}
          <span style={{fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.28em',color:SP.ink,fontWeight:600,textTransform:'uppercase'}}>{eyebrow}</span>
        </div>
        <h2 style={{fontFamily:SERIF,fontSize:'clamp(36px,4.8vw,64px)',lineHeight:1.04,letterSpacing:'-0.032em',color:SP.ink,fontWeight:400,margin:0,maxWidth:'860px'}}>
          {title} <em style={{fontStyle:'italic',background:SP.accentGrad,WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',display:'inline-block',paddingRight:'0.12em',paddingBottom:'0.08em'}}>{em}</em>{tail}
        </h2>
      </div>
      {tagline && <p style={{fontFamily:SERIF,fontSize:'16px',lineHeight:1.55,color:SP.muted,margin:0,maxWidth:'360px',fontStyle:'italic'}}>{tagline}</p>}
    </div>
  );
}

function StatusPill({status}){
  if (status==='live') return <span style={{display:'inline-flex',alignItems:'center',gap:'6px',padding:'4px 10px',borderRadius:'999px',background:'rgba(16,185,129,0.09)',border:'1px solid rgba(16,185,129,0.28)',fontFamily:MONO,fontSize:'11px',letterSpacing:'0.22em',color:'#047857',fontWeight:700,textTransform:'uppercase'}}><span style={{width:'5px',height:'5px',borderRadius:'50%',background:SP.signal}}/>Live today</span>;
  if (status==='by-request') return <span style={{display:'inline-flex',alignItems:'center',gap:'6px',padding:'4px 10px',borderRadius:'999px',background:'transparent',border:`1px solid ${SP.line}`,fontFamily:MONO,fontSize:'11px',letterSpacing:'0.22em',color:SP.muted,fontWeight:700,textTransform:'uppercase'}}>By request</span>;
  return <span style={{display:'inline-flex',alignItems:'center',gap:'6px',padding:'4px 10px',borderRadius:'999px',background:'transparent',border:`1px dashed ${SP.accentTintB}`,fontFamily:MONO,fontSize:'11px',letterSpacing:'0.22em',color:SP.accentDeep,fontWeight:700,textTransform:'uppercase'}}><span style={{width:'5px',height:'5px',borderRadius:'50%',border:`1px solid ${SP.accentA}`}}/>On the roadmap</span>;
}

function Btn({children,href=MAIL}){
  const s={display:'inline-flex',alignItems:'center',padding:'16px 34px',borderRadius:'999px',background:SP.ink,color:'#000',fontFamily:MONO,fontSize:'11px',fontWeight:700,letterSpacing:'0.24em',textTransform:'uppercase',textDecoration:'none',boxShadow:'0 14px 32px -14px rgba(124,58,237,0.55)',transition:'transform 0.2s,box-shadow 0.2s'};
  const E=e=>{e.currentTarget.style.transform='translateY(-1px)';e.currentTarget.style.boxShadow='0 20px 40px -16px rgba(124,58,237,0.7)';};
  const L=e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow='0 14px 32px -14px rgba(124,58,237,0.55)';};
  return <a href={href} style={s} onMouseEnter={E} onMouseLeave={L}>{children}</a>;
}
function Ghost({children,trailing,href=MAIL}){
  const s={display:'inline-flex',alignItems:'center',gap:'12px',padding:'16px 32px',borderRadius:'999px',background:'rgba(255,255,255,0.03)',backdropFilter:'blur(20px)',color:SP.ink,border:`1px solid ${SP.line}`,fontFamily:MONO,fontSize:'11px',fontWeight:700,letterSpacing:'0.24em',textTransform:'uppercase',textDecoration:'none',transition:'background 0.2s,border 0.2s,transform 0.2s'};
  const E=e=>{e.currentTarget.style.background='rgba(255,255,255,0.08)';e.currentTarget.style.borderColor=SP.accentTintB;e.currentTarget.style.transform='translateY(-1px)';};
  const L=e=>{e.currentTarget.style.background='rgba(255,255,255,0.03)';e.currentTarget.style.borderColor=SP.line;e.currentTarget.style.transform='none';};
  return <a href={href} style={s} onMouseEnter={E} onMouseLeave={L}><span>{children}</span>{trailing && <span style={{fontSize:'14px',letterSpacing:0,transform:'translateY(-1px)'}}>{trailing}</span>}</a>;
}

/* ═══ 1 · DATA CENTER HERO ═══ */
function DataCenterHero() {
  return (
    <section style={{ position: 'relative', width: '100%', height: '100vh', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <video 
        src="https://daggpt.network/uploads/daggpt_data_center_design.mp4" 
        autoPlay 
        muted 
        loop 
        playsInline
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.9 }}
      />
      {/* Subtle overlay so it blends perfectly with the dark UI below */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '30vh', background: 'linear-gradient(to top, rgba(0,0,0,1), transparent)' }} />
      
    </section>
  );
}

/* ═══ 2 · ENTERPRISE INTRO (Formally Masthead) ═══ */
function EnterpriseIntro(){
  const {m,t} = useBp();
  return (
    <section style={{position:'relative',zIndex:1,maxWidth:'1400px',margin:'0 auto',padding: m?'64px 20px 48px':t?'80px 32px 72px':'96px 48px 96px'}}>
      <div style={{display:'grid',gridTemplateColumns: m||t?'1fr':'1.55fr 1fr',gap: m?'48px':'56px',alignItems:'end'}}>
        <div>
          <motion.h1 initial={{opacity:0,y:32}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:1,ease:[0.22,1,0.36,1],delay:0.1}}
            style={{fontFamily:SERIF,fontSize:'clamp(44px,6.4vw,92px)',lineHeight:1.02,letterSpacing:'-0.038em',color:SP.ink,fontWeight:400,margin:0}}>
            AI infrastructure that <em style={{fontStyle:'italic',background:SP.accentGrad,WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',display:'inline-block',paddingRight:'0.12em',paddingBottom:'0.08em'}}>answers to you</em>.
          </motion.h1>
          <motion.p initial={{opacity:0,y:10}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:0.2,duration:0.7}}
            style={{fontFamily:SERIF,fontSize:m?'18px':'22px',lineHeight:1.5,color:SP.inkSoft,fontStyle:'italic',maxWidth:'620px',margin:'32px 0 0'}}>
            SSO-ready today. Your data never trains our models. Built for teams who can't afford to leak.
          </motion.p>
          <motion.div initial={{opacity:0,y:10}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:0.3,duration:0.6}}
            style={{display:'inline-flex',gap:'14px',flexWrap:'wrap',marginTop:'44px'}}>
            <Btn>Contact sales</Btn>
            <Ghost href="#data-flow" trailing="↓">See how your data flows</Ghost>
          </motion.div>
        </div>
        {!m && <motion.div initial={{opacity:0,y:20,scale:0.98}} whileInView={{opacity:1,y:0,scale:1}} viewport={{once:true}}
          transition={{duration:1,delay:0.4,ease:[0.22,1,0.36,1]}}><PrivacyPromiseCard/></motion.div>}
      </div>
      <motion.div initial={{opacity:0,y:16}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:0.5,duration:0.8}}
        style={{display:'grid',gridTemplateColumns: m?'repeat(2,1fr)':'repeat(4,1fr)',marginTop: m?'56px':'96px',borderTop:`1px solid ${SP.line}`}}>
        {[{k:'0',v:'Training on your data'},{k:'400+',v:'Models, one interface'},{k:'14',v:'Frontier providers'},{k:'1',v:'Unified invoice'}].map((s,i)=>(
          <div key={s.v} style={{padding:m?'22px 16px':'28px 24px',
            borderRight: (!m && i<3)||(m && i%2===0)?`1px solid ${SP.line}`:'none',
            borderBottom: m && i<2?`1px solid ${SP.line}`:'none'}}>
            <div style={{fontFamily:SERIF,fontSize:m?'28px':'42px',lineHeight:1,color:SP.ink,fontWeight:400,letterSpacing:'-0.03em',marginBottom:'10px'}}>{s.k}</div>
            <div style={{fontFamily:MONO,fontSize:'11px',letterSpacing:'0.2em',color:SP.muted,fontWeight:600,textTransform:'uppercase'}}>{s.v}</div>
          </div>
        ))}
      </motion.div>
    </section>
  );
}

function PrivacyPromiseCard(){
  const F = [
    {k:'0',v:'Training runs on your data.',sub:'Not prompts. Not outputs. Not metadata.'},
    {k:'1',v:'Unified bill, one login.',sub:'Replace twelve API keys with one relationship.'},
    {k:'24h',v:'Purge window on deletion.',sub:'No "just in case" backups.'},
  ];
  return (
    <div style={{position:'relative',background:'rgba(255,255,255,0.03)',backdropFilter:'blur(20px)',WebkitBackdropFilter:'blur(20px)',border:`1px solid ${SP.line}`,borderRadius:'24px',padding:'28px',overflow:'hidden',boxShadow:'0 4px 24px -1px rgba(0,0,0,0.2)'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'22px'}}>
        <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
          <motion.span animate={{scale:[1,1.35,1],opacity:[1,0.5,1]}} transition={{duration:2,repeat:Infinity,ease:'easeInOut'}} style={{width:'7px',height:'7px',borderRadius:'50%',background:SP.signal}}/>
          <span style={{fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.22em',color:SP.ink,fontWeight:700,textTransform:'uppercase'}}>Privacy by default</span>
        </div>
        <span style={{fontFamily:MONO,fontSize:'11px',letterSpacing:'0.18em',color:SP.muted,fontWeight:600}}>Promise</span>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:'14px'}}>
        {F.map((f,i)=>(
          <motion.div key={f.k} initial={{opacity:0,x:-8}} animate={{opacity:1,x:0}} transition={{delay:0.5+i*0.12,duration:0.6}}
            style={{display:'grid',gridTemplateColumns:'80px 1fr',gap:'14px',alignItems:'start',
              paddingBottom: i<F.length-1?'14px':0,borderBottom: i<F.length-1?`1px solid ${SP.lineSoft}`:'none'}}>
            <div style={{fontFamily:SERIF,fontSize:'46px',lineHeight:0.9,color:SP.ink,fontWeight:400,letterSpacing:'-0.04em'}}>{f.k}</div>
            <div>
              <div style={{fontFamily:SERIF,fontSize:'17px',lineHeight:1.25,color:SP.ink,fontWeight:500,letterSpacing:'-0.018em',marginBottom:'4px'}}>{f.v}</div>
              <div style={{fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.06em',color:SP.muted,fontWeight:500,lineHeight:1.5}}>{f.sub}</div>
            </div>
          </motion.div>
        ))}
      </div>
      <div style={{marginTop:'20px',paddingTop:'18px',borderTop:`1px solid ${SP.lineSoft}`,display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:'6px'}}>
        <span style={{fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.2em',color:SP.accentDeep,fontWeight:700,textTransform:'uppercase'}}>Enterprise SSO</span>
        <span style={{fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.18em',color:SP.signal,fontWeight:700}}>✓ Live today</span>
      </div>
    </div>
  );
}

/* ═══ 2 · PRIVACY MOMENT ═══ */
function PrivacyMoment(){
  const {m,t} = useBp();
  const C = [
    {k:'0',title:'Zero training on customer data. Ever.',body:'Not on your prompts, not on your outputs, not on metadata. Contractually, technically, and by default.'},
    {k:'∞',title:'You control retention.',body:'Save what you want, forever. Delete anything, anytime. No dark-pattern exports, no "just in case" backups.'},
    {k:'1',title:'One unified bill.',body:'One invoice, one login, one admin console. Stop managing twelve vendor API keys across three finance cycles.'},
  ];
  return (
    <section style={{position:'relative',zIndex:1,maxWidth:'1400px',margin:'0 auto',padding: m?'0 20px 88px':t?'0 32px 110px':'0 48px 140px'}}>
      <SectionHead eyebrow="The promise" title="Your prompts. Your outputs." em="Your data" tagline="Stop paying for features you already turn off. The privacy posture enterprise buyers actually want, in the product by default."/>
      <div style={{display:'grid',gridTemplateColumns: m?'1fr':'repeat(3,1fr)',gap: m?'16px':'20px'}}>
        {C.map((c,i)=>(
          <motion.div key={c.k} initial={{opacity:0,y:28}} whileInView={{opacity:1,y:0}} viewport={{once:true,amount:0.25}} transition={{duration:0.7,delay:i*0.1,ease:[0.22,1,0.36,1]}}
            style={{position:'relative',background:'rgba(255,255,255,0.03)',backdropFilter:'blur(20px)',WebkitBackdropFilter:'blur(20px)',border:`1px solid ${SP.line}`,borderRadius:'24px',padding: m?'28px 24px 32px':'36px 32px 40px',overflow:'hidden',boxShadow:'0 4px 24px -1px rgba(0,0,0,0.2)'}}>
            <div style={{position:'absolute',top:'-20%',right:'-15%',width:'260px',height:'260px',background:'radial-gradient(circle,rgba(124,58,237,0.09) 0%,transparent 60%)',pointerEvents:'none'}}/>
            <div style={{position:'relative',fontFamily:SERIF,fontSize:'clamp(86px,8.8vw,136px)',lineHeight:0.88,fontWeight:400,letterSpacing:'-0.05em',marginBottom:'18px',background:SP.accentGrad,WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent'}}>{c.k}</div>
            <div style={{position:'relative',fontFamily:SERIF,fontSize:'22px',lineHeight:1.2,letterSpacing:'-0.02em',color:SP.ink,fontWeight:400,marginBottom:'10px'}}>{c.title}</div>
            <div style={{position:'relative',fontSize:'13.5px',lineHeight:1.6,color:SP.muted}}>{c.body}</div>
          </motion.div>
        ))}
      </div>
      <div style={{marginTop: m?'32px':'48px',padding: m?'22px 24px':'28px 32px',border:`1px solid ${SP.accentTintB}`,borderRadius:'20px',background:SP.accentTint,display:'flex',gap:'20px',alignItems:'center',flexWrap:'wrap'}}>
        <ShieldCheck size={22} color={SP.accentA} strokeWidth={1.8}/>
        <p style={{margin:0,flex:1,minWidth:'260px',fontFamily:SERIF,fontSize:'16px',lineHeight:1.55,color:SP.inkSoft,fontStyle:'italic'}}>
          We don't retain your prompts beyond the session unless you save them. When you delete, it's gone within 24 hours. <span style={{color:SP.ink,fontWeight:500,fontStyle:'normal'}}>No fine-print exceptions, no "legitimate interest" carve-outs.</span>
        </p>
      </div>
    </section>
  );
}

/* ═══ 3 · MODEL ROSTER ═══ */
function ModelRoster(){
  const {m,t} = useBp();
  const B = [
    {k:'01',cat:'Chat & reasoning',ms:['GPT-5','Claude 4.5 Sonnet','Claude 4.5 Opus','Gemini 2.5','Gemini 3','Llama 4','DeepSeek 3.2','Mistral Large','Qwen','Grok']},
    {k:'02',cat:'Image',           ms:['Imagen 4','Flux Pro','Stable Diffusion XL','DALL·E 3','Ideogram','Recraft']},
    {k:'03',cat:'Video',           ms:['Veo 3.1','Sora','Runway Gen-3','Kling','Pika','Luma Dream Machine']},
    {k:'04',cat:'Audio & music',   ms:['Lyria 2','Suno v4','ElevenLabs','Whisper','Play HT']},
  ];
  return (
    <section style={{position:'relative',zIndex:1,maxWidth:'1400px',margin:'0 auto',padding: m?'0 20px 88px':t?'0 32px 110px':'0 48px 140px'}}>
      <SectionHead icon={Layers} eyebrow="The roster" title="Every model worth running." em="One bill" tagline="Frontier models across chat, image, video, and audio — each routed through the same interface, the same login, the same invoice."/>
      <div style={{display:'flex',flexDirection:'column',gap: m?'16px':'18px'}}>
        {B.map((b,i)=>(
          <motion.div key={b.k} initial={{opacity:0,y:24}} whileInView={{opacity:1,y:0}} viewport={{once:true,amount:0.2}} transition={{duration:0.7,delay:i*0.08,ease:[0.22,1,0.36,1]}}
            style={{position:'relative',background:'rgba(255,255,255,0.03)',backdropFilter:'blur(20px)',WebkitBackdropFilter:'blur(20px)',border:`1px solid ${SP.line}`,borderRadius:'22px',padding: m?'22px 22px 24px':'28px 32px 30px',display:'grid',gridTemplateColumns: m||t?'1fr':'0.32fr 1fr',gap: m?'14px':'32px',alignItems:'center',boxShadow:'0 1px 2px rgba(10,10,10,0.03)'}}>
            <div>
              <div style={{fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.22em',color:SP.accentA,fontWeight:700,marginBottom:'6px'}}>{b.k}</div>
              <div style={{fontFamily:SERIF,fontSize:'26px',lineHeight:1.05,letterSpacing:'-0.025em',color:SP.ink,fontWeight:400}}>{b.cat}</div>
            </div>
            <div style={{display:'flex',flexWrap:'wrap',gap:'8px'}}>
              {b.ms.map(n=>(
                <span key={n} style={{padding:'7px 14px',borderRadius:'999px',background:SP.canvasCool,border:`1px solid ${SP.lineSoft}`,fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.02em',color:SP.inkSoft,fontWeight:500}}>{n}</span>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
      <div style={{marginTop: m?'28px':'40px',paddingTop:'22px',borderTop:`1px solid ${SP.line}`,display:'flex',gap:'28px',flexWrap:'wrap',justifyContent:'space-between',alignItems:'center'}}>
        <p style={{margin:0,fontFamily:SERIF,fontSize:'16px',lineHeight:1.55,color:SP.muted,fontStyle:'italic',maxWidth:'540px'}}>
          Route image work to Imagen, reasoning to Claude, cost-sensitive throughput to DeepSeek — in the same workspace. Swap providers without re-integrating.
        </p>
        <div style={{display:'flex',gap:'22px',flexWrap:'wrap'}}>
          {[['400+','models'],['14','providers'],['1','invoice'],['0','lock-in']].map(x=>(
            <div key={x[0]}>
              <div style={{fontFamily:SERIF,fontSize:'28px',lineHeight:1,color:SP.ink,fontWeight:400,letterSpacing:'-0.025em'}}>{x[0]}</div>
              <div style={{fontFamily:MONO,fontSize:'11px',letterSpacing:'0.2em',color:SP.muted,fontWeight:600,textTransform:'uppercase',marginTop:'4px'}}>{x[1]}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══ 4 · DATA FLOW ═══ */
function DataFlow(){
  const {m,t} = useBp();
  const H = [
    {k:'01',l:'Your prompt',    b:'Encrypted in transit (TLS 1.3). Never logged in plaintext.'},
    {k:'02',l:'Our proxy',      b:'Routes to the provider you chose. We see it in-flight, we don\'t store it.'},
    {k:'03',l:'Model provider', b:'Zero-retention agreements with frontier labs where available.'},
    {k:'04',l:'Response',       b:'Streams back to you. Stored only if you explicitly save.'},
    {k:'05',l:'Your deletion',  b:'Purged from our systems within 24 hours. No backups held hostage.'},
  ];
  const N = ['Train our models on your content','Sell or share your data with third parties','Retain prompts beyond session unless saved','Keep "just in case" backups of deleted content'];
  return (
    <section id="data-flow" style={{position:'relative',zIndex:1,margin: m?'0 16px 88px':t?'0 24px 110px':'0 48px 140px',borderRadius: m?'24px':'32px',overflow:'hidden',background:'rgba(255,255,255,0.03)',backdropFilter:'blur(20px)',WebkitBackdropFilter:'blur(20px)',boxShadow:'0 60px 140px -60px rgba(124,58,237,0.38),inset 0 1px 0 rgba(255,255,255,0.04)',border:'1px solid rgba(167,139,250,0.16)'}}>
      <div style={{position:'absolute',top:'-40%',right:'-10%',width:'640px',height:'640px',background:'radial-gradient(circle at 60% 40%,rgba(124,58,237,0.35) 0%,transparent 62%)',filter:'blur(40px)',pointerEvents:'none'}}/>
      <div style={{position:'relative',padding: m?'40px 22px 44px':t?'56px 40px':'72px 64px'}}>
        <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'18px'}}>
          <span style={{width:'6px',height:'6px',borderRadius:'50%',background:SP.accentC}}/>
          <span style={{fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.26em',color:SP.accentC,fontWeight:700,textTransform:'uppercase'}}>The data flow</span>
        </div>
        <h2 style={{fontFamily:SERIF,fontSize:'clamp(34px,5vw,68px)',lineHeight:1.03,letterSpacing:'-0.032em',color:'#FFF',fontWeight:400,margin:'0 0 14px',maxWidth:'860px'}}>
          Five stages, <em style={{fontStyle:'italic',background:'linear-gradient(135deg,#C4B5FD 0%,#E9D5FF 100%)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',display:'inline-block',paddingRight:'0.12em',paddingBottom:'0.08em'}}>nothing sticky</em>.
        </h2>
        <p style={{fontSize:'16px',lineHeight:1.6,color:'rgba(255,255,255,0.68)',maxWidth:'620px',margin:'0 0 40px'}}>
          How a request moves through DAGGPT, start to purge. No fine-print exceptions — this is the architecture.
        </p>
        <DataFlowDiagram mobile={m}/>
        <div style={{display:'grid',gridTemplateColumns: m||t?'1fr':'1.3fr 1fr',gap: m?'28px':'48px',alignItems:'flex-start'}}>
          <ol style={{listStyle:'none',margin:0,padding:0}}>
            {H.map((h,i)=>(
              <li key={h.k} style={{display:'grid',gridTemplateColumns:'50px 1fr',gap:'16px',padding:'18px 0',borderBottom: i<H.length-1?'1px solid rgba(255,255,255,0.06)':'none'}}>
                <div style={{fontFamily:MONO,fontSize:'11px',letterSpacing:'0.2em',color:SP.accentC,fontWeight:700}}>{h.k}</div>
                <div>
                  <div style={{fontFamily:SERIF,fontSize:'22px',lineHeight:1.15,color:'#FFF',fontWeight:400,letterSpacing:'-0.02em',marginBottom:'6px'}}>{h.l}</div>
                  <div style={{fontSize:'13.5px',lineHeight:1.55,color:'rgba(255,255,255,0.65)'}}>{h.b}</div>
                </div>
              </li>
            ))}
          </ol>
          <div style={{background:'rgba(10,10,25,0.45)',border:'1px solid rgba(167,139,250,0.18)',borderRadius:'18px',padding:'24px 26px'}}>
            <div style={{fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.24em',color:SP.accentC,fontWeight:700,textTransform:'uppercase',marginBottom:'18px'}}>What we don't do</div>
            <ul style={{listStyle:'none',margin:0,padding:0}}>
              {N.map((n,i)=>(
                <li key={n} style={{display:'flex',gap:'12px',padding:'10px 0',borderBottom: i<N.length-1?'1px dashed rgba(255,255,255,0.08)':'none'}}>
                  <span style={{color:'#F87171',fontFamily:MONO,fontSize:'12px',fontWeight:700,flexShrink:0}}>✗</span>
                  <span style={{fontSize:'13.5px',lineHeight:1.5,color:'rgba(255,255,255,0.78)'}}>{n}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══ 5 · PRINCIPLES ═══ */
function Principles(){
  const {m,t} = useBp();
  const P = [
    {k:'01',big:'Capability without control is a liability.',sub:'Frontier AI is only useful to enterprises if it respects the rules enterprises live by.'},
    {k:'02',big:'One vendor, many models.',sub:'You shouldn\'t have to choose a provider — you should choose a problem and pick the best model for it.'},
    {k:'03',big:'Your data is yours — we shouldn\'t have to say that.',sub:'And yet, we\'ll say it in the contract, enforce it in the stack, and prove it in the architecture.'},
  ];
  return (
    <section style={{position:'relative',zIndex:1,maxWidth:'1100px',margin:'0 auto',padding: m?'0 20px 88px':t?'0 32px 110px':'20px 40px 140px'}}>
      <div style={{display:'flex',alignItems:'center',gap:'16px',paddingBottom:'14px',borderBottom:`1px solid ${SP.line}`,marginBottom: m?'40px':'56px'}}>
        <span style={{width:'6px',height:'6px',borderRadius:'50%',background:SP.accentGrad}}/>
        <span style={{fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.28em',color:SP.ink,fontWeight:600,textTransform:'uppercase'}}>What we believe</span>
        <span style={{flex:1,height:'1px',background:SP.line}}/>
        <span style={{fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.2em',color:SP.muted,fontWeight:600}}>03</span>
      </div>
      {P.map((p,i)=>(
        <motion.div key={p.k} initial={{opacity:0,y:18}} whileInView={{opacity:1,y:0}} viewport={{once:true,amount:0.3}} transition={{duration:0.8,delay:i*0.1,ease:[0.22,1,0.36,1]}}
          style={{display:'grid',gridTemplateColumns: m?'1fr':'80px 1fr',gap: m?'12px':'32px',padding: m?'28px 0':'40px 0',borderBottom: i<P.length-1?`1px solid ${SP.lineSoft}`:'none'}}>
          <div style={{fontFamily:MONO,fontSize:'11px',letterSpacing:'0.22em',color:SP.accentA,fontWeight:700,paddingTop:'10px'}}>{p.k}</div>
          <div>
            <div style={{fontFamily:SERIF,fontSize:'clamp(28px,3.6vw,46px)',lineHeight:1.12,letterSpacing:'-0.028em',color:SP.ink,fontWeight:400,marginBottom:'14px'}}>{p.big}</div>
            <div style={{fontFamily:SERIF,fontStyle:'italic',fontSize:'17px',lineHeight:1.55,color:SP.muted,maxWidth:'640px'}}>{p.sub}</div>
          </div>
        </motion.div>
      ))}
    </section>
  );
}

/* ═══ 6 · SPEC SHEET ═══ */
function SpecSheet(){
  const {m,t} = useBp();
  const [idx,setIdx] = useState(0);
  if (m) return <SpecMobile/>;
  const a = CAPS[idx];
  return (
    <section id="spec" style={{position:'relative',zIndex:1,maxWidth:'1400px',margin:'0 auto',padding:t?'0 32px 120px':'0 48px 160px'}}>
      <SectionHead eyebrow="Spec sheet" title="What's live. What's" em="coming" tagline="Twelve primitives. Six shipping today, six on the roadmap — each clearly labelled. No magic ink, no moving targets."/>
      <div style={{display:'grid',gridTemplateColumns:t?'1fr':'0.92fr 1.4fr',gap:t?'32px':'56px',alignItems:'flex-start'}}>
        <div style={{position:'sticky',top:'120px'}}>
          <div style={{display:'grid',gridTemplateColumns:'16px 30px 1fr auto',gap:'12px',paddingBottom:'14px',borderBottom:`1px solid ${SP.line}`,fontFamily:MONO,fontSize:'11px',letterSpacing:'0.24em',color:SP.muted,fontWeight:700,textTransform:'uppercase'}}>
            <span></span><span>No.</span><span>Chapter</span><span>Focus</span>
          </div>
          <ul style={{listStyle:'none',margin:0,padding:0}}>
            {CAPS.map((c,i)=>{
              const A = i===idx;
              const dF = c.status==='live' ? SP.signal : 'transparent';
              const dB = c.status==='live' ? SP.signal : SP.accentA;
              return (
                <li key={c.num}>
                  <button onMouseEnter={()=>setIdx(i)} onFocus={()=>setIdx(i)} onClick={()=>setIdx(i)}
                    style={{all:'unset',cursor:'pointer',width:'100%',display:'grid',gridTemplateColumns:'16px 30px 1fr auto',alignItems:'center',gap:'12px',padding:'14px 0',borderBottom:`1px solid ${SP.lineSoft}`,position:'relative'}}>
                    <motion.span aria-hidden animate={{opacity:A?1:0,scaleY:A?1:0.3}} transition={{type:'spring',stiffness:380,damping:32}}
                      style={{position:'absolute',left:'-14px',top:'18%',bottom:'18%',width:'3px',borderRadius:'999px',background:SP.accentGrad,transformOrigin:'center'}}/>
                    <span style={{width:'8px',height:'8px',borderRadius:'50%',background:dF,border:`1.5px solid ${dB}`,boxSizing:'border-box'}}/>
                    <span style={{fontFamily:MONO,fontSize:'11px',letterSpacing:'0.14em',color:A?SP.accentA:SP.subtle,fontWeight:700,transition:'color 0.2s'}}>{c.num}</span>
                    <span style={{fontFamily:SERIF,fontSize:A?'21px':'17px',color:A?SP.ink:SP.muted,fontWeight:400,fontStyle:A?'italic':'normal',letterSpacing:'-0.015em',lineHeight:1.2,transition:'all 0.28s cubic-bezier(0.22,1,0.36,1)'}}>{c.title}</span>
                    <motion.span animate={{x:A?0:-4,opacity:A?1:0.25}} transition={{duration:0.25}} style={{fontFamily:MONO,fontSize:'11px',color:SP.accentA,fontWeight:700}}>→</motion.span>
                  </button>
                </li>
              );
            })}
          </ul>
          <div style={{marginTop:'18px',display:'flex',gap:'18px',flexWrap:'wrap',fontFamily:MONO,fontSize:'11px',letterSpacing:'0.2em',color:SP.muted,fontWeight:600,textTransform:'uppercase'}}>
            <span style={{display:'inline-flex',alignItems:'center',gap:'6px'}}><span style={{width:'7px',height:'7px',borderRadius:'50%',background:SP.signal}}/>Live</span>
            <span style={{display:'inline-flex',alignItems:'center',gap:'6px'}}><span style={{width:'7px',height:'7px',borderRadius:'50%',background:'transparent',border:`1.5px solid ${SP.accentA}`,boxSizing:'border-box'}}/>Roadmap</span>
          </div>
        </div>
        <Detail a={a}/>
      </div>
    </section>
  );
}

function Detail({a}){
  const I = a.icon;
  return (
    <div style={{position:'relative',background:'rgba(255,255,255,0.03)',backdropFilter:'blur(20px)',border:`1px solid ${SP.line}`,borderRadius:'28px',overflow:'hidden',minHeight:'560px',boxShadow:'0 1px 2px rgba(0,0,0,0.2),0 40px 100px -50px rgba(124,58,237,0.15)'}}>
      <div style={{position:'absolute',top:'-30%',right:'-20%',width:'520px',height:'520px',background:'radial-gradient(circle,rgba(124,58,237,0.13) 0%,transparent 60%)',pointerEvents:'none'}}/>
      <div style={{position:'absolute',right:'-4%',bottom:'-18%',fontFamily:SERIF,fontSize:'480px',lineHeight:1,fontWeight:400,letterSpacing:'-0.05em',color:'rgba(124,58,237,0.055)',pointerEvents:'none',userSelect:'none'}}>{a.num}</div>
      <AnimatePresence mode="wait">
        <motion.div key={a.num} initial={{opacity:0,y:16}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-8}} transition={{duration:0.45,ease:[0.22,1,0.36,1]}}
          style={{position:'relative',padding:'44px 48px 40px',display:'flex',flexDirection:'column',minHeight:'560px'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'18px',flexWrap:'wrap',marginBottom:'32px'}}>
            <div style={{display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap'}}>
              <span style={{display:'inline-flex',alignItems:'center',padding:'6px 12px',borderRadius:'999px',background:SP.accentTint,border:`1px solid ${SP.accentTintB}`,fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.22em',color:SP.accentDeep,fontWeight:700,textTransform:'uppercase'}}>{a.tag}</span>
              <StatusPill status={a.status}/>
            </div>
            <div style={{width:'56px',height:'56px',borderRadius:'16px',background:SP.accentTint,border:`1px solid ${SP.accentTintB}`,display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 10px 24px -12px rgba(124,58,237,0.4)'}}>
              <I size={26} color={SP.accentA} strokeWidth={1.6}/>
            </div>
          </div>
          <h3 style={{fontFamily:SERIF,fontSize:'clamp(36px,4vw,56px)',lineHeight:1.02,letterSpacing:'-0.032em',color:SP.ink,fontWeight:400,margin:'0 0 20px'}}>{a.title}</h3>
          <p style={{fontFamily:SERIF,fontStyle:'italic',fontSize:'19px',lineHeight:1.5,color:SP.inkSoft,margin:'0 0 36px',maxWidth:'580px'}}>“{a.body}”</p>
          <div style={{marginTop:'auto',display:'grid',gridTemplateColumns:'1fr 1fr',borderTop:`1px solid ${SP.line}`}}>
            <div style={{padding:'20px 24px 4px 0',borderRight:`1px solid ${SP.line}`}}>
              <div style={{fontFamily:MONO,fontSize:'11px',letterSpacing:'0.24em',color:SP.muted,fontWeight:700,textTransform:'uppercase',marginBottom:'10px'}}>Standard</div>
              <div style={{fontFamily:SERIF,fontSize:'17px',color:SP.ink,fontWeight:400,lineHeight:1.35}}>{a.std}</div>
            </div>
            <div style={{padding:'20px 0 4px 24px'}}>
              <div style={{fontFamily:MONO,fontSize:'11px',letterSpacing:'0.24em',color:SP.muted,fontWeight:700,textTransform:'uppercase',marginBottom:'10px'}}>Scope</div>
              <div style={{fontFamily:SERIF,fontSize:'17px',color:SP.ink,fontWeight:400,lineHeight:1.35}}>{a.scope}</div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function SpecMobile(){
  return (
    <section id="spec" style={{position:'relative',zIndex:1,padding:'0 20px 80px'}}>
      <div style={{marginBottom:'36px',paddingBottom:'18px',borderBottom:`1px solid ${SP.line}`}}>
        <div style={{display:'inline-flex',alignItems:'center',gap:'8px',marginBottom:'14px'}}>
          <span style={{width:'6px',height:'6px',borderRadius:'50%',background:SP.accentGrad}}/>
          <span style={{fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.24em',color:SP.ink,fontWeight:600,textTransform:'uppercase'}}>Spec sheet</span>
        </div>
        <h2 style={{fontFamily:SERIF,fontSize:'38px',lineHeight:1.02,letterSpacing:'-0.03em',color:SP.ink,fontWeight:400,margin:0}}>
          What's live. What's <em style={{fontStyle:'italic',background:SP.accentGrad,WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',display:'inline-block',paddingRight:'0.12em',paddingBottom:'0.08em'}}>coming</em>.
        </h2>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:'14px'}}>
        {CAPS.map(c=>{
          const I = c.icon;
          return (
            <motion.article key={c.num} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true,amount:0.2}} transition={{duration:0.6}}
              style={{position:'relative',background:'rgba(255,255,255,0.03)',backdropFilter:'blur(20px)',border:`1px solid ${SP.line}`,borderRadius:'20px',padding:'22px 22px 20px',overflow:'hidden'}}>
              <div style={{position:'absolute',right:'-12%',bottom:'-30%',fontFamily:SERIF,fontSize:'200px',fontWeight:400,color:'rgba(124,58,237,0.05)',lineHeight:1,pointerEvents:'none',userSelect:'none'}}>{c.num}</div>
              <div style={{position:'relative',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'10px',flexWrap:'wrap',marginBottom:'14px'}}>
                <StatusPill status={c.status}/>
                <I size={18} color={SP.accentA} strokeWidth={1.6}/>
              </div>
              <h3 style={{position:'relative',fontFamily:SERIF,fontSize:'24px',lineHeight:1.1,letterSpacing:'-0.025em',color:SP.ink,fontWeight:400,margin:'0 0 10px'}}>{c.title}</h3>
              <p style={{position:'relative',fontSize:'14px',lineHeight:1.55,color:SP.muted,margin:'0 0 16px'}}>{c.body}</p>
              <div style={{position:'relative',display:'flex',flexWrap:'wrap',gap:'14px 20px',paddingTop:'12px',borderTop:`1px solid ${SP.lineSoft}`}}>
                <div><div style={{fontFamily:MONO,fontSize:'11px',letterSpacing:'0.22em',color:SP.muted,fontWeight:700,textTransform:'uppercase'}}>Standard</div>
                  <div style={{fontFamily:SERIF,fontSize:'13px',color:SP.ink,marginTop:'3px'}}>{c.std}</div></div>
                <div><div style={{fontFamily:MONO,fontSize:'11px',letterSpacing:'0.22em',color:SP.muted,fontWeight:700,textTransform:'uppercase'}}>Scope</div>
                  <div style={{fontFamily:SERIF,fontSize:'13px',color:SP.ink,marginTop:'3px'}}>{c.scope}</div></div>
              </div>
            </motion.article>
          );
        })}
      </div>
    </section>
  );
}

/* ═══ 7 · PLATFORM & API ═══ */
function PlatformAPI(){
  const {m,t} = useBp();
  const C = [
    {icon:Terminal, title:'REST API',   status:'live',    body:'Unified endpoint. One auth key, every model. Streaming responses. OpenAI-compatible format for drop-in migration.'},
    {icon:Braces,   title:'SDKs',       status:'live',    body:'Official Python and TypeScript SDKs. Community Go, Rust, Ruby. npm · PyPI · typed end-to-end.'},
    {icon:UserCog,  title:'Admin API',  status:'roadmap', body:'Programmatic workspace, seat, and budget management. Provision via Terraform when it ships.'},
    {icon:Webhook,  title:'Event hooks',status:'roadmap', body:'Audit-stream webhook subscriptions. Generation-complete callbacks. Budget-threshold alerts.'},
  ];
  return (
    <section style={{position:'relative',zIndex:1,maxWidth:'1400px',margin:'0 auto',padding: m?'0 20px 88px':t?'0 32px 110px':'0 48px 140px'}}>
      <SectionHead icon={Terminal} eyebrow="Platform & API" title="A product today." em="A platform underneath" tagline="The same API powers our web app, our workspace console, and the builder you see on daggpt.network. What we use, you can use."/>
      <div style={{display:'grid',gridTemplateColumns: m?'1fr':t?'repeat(2,1fr)':'repeat(4,1fr)',gap: m?'14px':'18px',marginBottom: m?'28px':'40px'}}>
        {C.map((c,i)=>{
          const I = c.icon;
          return (
            <motion.div key={c.title} initial={{opacity:0,y:22}} whileInView={{opacity:1,y:0}} viewport={{once:true,amount:0.3}} transition={{duration:0.6,delay:i*0.07,ease:[0.22,1,0.36,1]}}
              style={{background:'rgba(255,255,255,0.03)',backdropFilter:'blur(20px)',WebkitBackdropFilter:'blur(20px)',border:`1px solid ${SP.line}`,borderRadius:'22px',padding:'24px 24px 22px',display:'flex',flexDirection:'column',gap:'14px',minHeight:'220px',boxShadow:'0 4px 24px -1px rgba(0,0,0,0.2)'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <div style={{width:'40px',height:'40px',borderRadius:'12px',background:SP.accentTint,border:`1px solid ${SP.accentTintB}`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <I size={18} color={SP.accentA} strokeWidth={1.8}/>
                </div>
                <StatusPill status={c.status}/>
              </div>
              <div style={{fontFamily:SERIF,fontSize:'22px',lineHeight:1.1,letterSpacing:'-0.02em',color:SP.ink,fontWeight:400}}>{c.title}</div>
              <div style={{fontSize:'13.5px',lineHeight:1.55,color:SP.muted,flex:1}}>{c.body}</div>
            </motion.div>
          );
        })}
      </div>
      <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true,amount:0.3}} transition={{duration:0.7,ease:[0.22,1,0.36,1]}}
        style={{position:'relative',borderRadius:'22px',overflow:'hidden',background:'linear-gradient(160deg,#0B0B1A 0%,#14112E 100%)',border:'1px solid rgba(167,139,250,0.16)',boxShadow:'0 40px 90px -50px rgba(124,58,237,0.3)'}}>
        <div style={{display:'flex',alignItems:'center',gap:'6px',padding:'14px 20px',borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
          <span style={{width:'9px',height:'9px',borderRadius:'50%',background:'#FF5F57'}}/>
          <span style={{width:'9px',height:'9px',borderRadius:'50%',background:'#FEBC2E'}}/>
          <span style={{width:'9px',height:'9px',borderRadius:'50%',background:'#28C840'}}/>
          <span style={{marginLeft:'14px',fontFamily:MONO,fontSize:'11px',color:'rgba(255,255,255,0.55)',letterSpacing:'0.1em'}}>example.py · daggpt SDK</span>
          <span style={{marginLeft:'auto',fontFamily:MONO,fontSize:'11.5px',color:'rgba(255,255,255,0.4)',letterSpacing:'0.18em'}}>OPENAI-COMPATIBLE</span>
        </div>
        <pre style={{margin:0,padding: m?'20px':'28px 32px',fontFamily:MONO,fontSize: m?'12.5px':'13.5px',lineHeight:1.7,color:'rgba(255,255,255,0.88)',overflow:'auto'}}>
{`from daggpt import Client

c = Client(api_key="dggpt_...")

response = c.chat.completions.create(
    model="claude-4.5-sonnet",
    messages=[{"role": "user", "content": "Draft a privacy-first DPA clause."}],
    workspace="legal",
)

print(response.choices[0].message.content)`}
        </pre>
      </motion.div>
      <p style={{margin:'28px 0 0',fontFamily:SERIF,fontStyle:'italic',fontSize:'15px',lineHeight:1.55,color:SP.muted,maxWidth:'620px'}}>
        Swap <code style={{fontFamily:MONO,fontSize:'13px',background:SP.canvasCool,padding:'2px 6px',borderRadius:'4px',color:SP.accentDeep}}>api.openai.com</code> for <code style={{fontFamily:MONO,fontSize:'13px',background:SP.canvasCool,padding:'2px 6px',borderRadius:'4px',color:SP.accentDeep}}>api.daggpt.network</code> and point 400+ models through the SDK you already use.
      </p>
    </section>
  );
}

/* ═══ 8 · VENDOR SPRAWL ═══ */
function VendorSprawl(){
  const {m,t} = useBp();
  const R = [
    ['12 vendor contracts to negotiate',    '1 contract'],
    ['12 API keys to rotate',                '1 key, every model'],
    ['12 invoices to reconcile',             '1 invoice'],
    ['12 security questionnaires to fill',   '1 security brief'],
    ['12 admin consoles to audit',           '1 admin console'],
    ['12 places your data could leak',       '1 perimeter, zero-training promise'],
  ];
  return (
    <section style={{position:'relative',zIndex:1,maxWidth:'1400px',margin:'0 auto',padding: m?'0 20px 88px':t?'0 32px 110px':'0 48px 140px'}}>
      <SectionHead eyebrow="The consolidation" title="Twelve vendors," em="or one" tagline="You still get every model. You just stop running a switchboard for your AI vendors."/>
      <div style={{display:'grid',gridTemplateColumns: m?'1fr':'1fr 1fr',border:`1px solid ${SP.line}`,borderRadius:'26px',overflow:'hidden',background:'rgba(255,255,255,0.03)',backdropFilter:'blur(20px)',WebkitBackdropFilter:'blur(20px)'}}>
        <div style={{padding: m?'28px 24px':'40px 36px',background:SP.canvas,borderRight: m?'none':`1px solid ${SP.line}`,borderBottom: m?`1px solid ${SP.line}`:'none'}}>
          <div style={{display:'inline-flex',alignItems:'center',gap:'8px',marginBottom:'18px',padding:'5px 11px',borderRadius:'999px',background:'rgba(255,255,255,0.05)',border:`1px solid ${SP.line}`,fontFamily:MONO,fontSize:'11px',letterSpacing:'0.22em',color:SP.muted,fontWeight:700,textTransform:'uppercase'}}>Before DAGGPT</div>
          <div style={{fontFamily:SERIF,fontSize:'clamp(28px,3.4vw,40px)',lineHeight:1.05,letterSpacing:'-0.028em',color:SP.muted,fontWeight:400,marginBottom:'22px',filter:'grayscale(0.4)'}}>
            Twelve vendors, each with their own console, contract, and invoice.
          </div>
          <div style={{display:'flex',flexWrap:'wrap',gap:'6px',marginBottom:'26px',opacity:0.55}}>
            {['OpenAI','Anthropic','Google','Meta','Mistral','xAI','Stability','RunwayML','ElevenLabs','Suno','DeepSeek','Qwen'].map(n=>(
              <span key={n} style={{padding:'5px 11px',borderRadius:'6px',background:'rgba(255,255,255,0.05)',border:`1px solid ${SP.line}`,fontFamily:MONO,fontSize:'12px',color:SP.muted,fontWeight:600,letterSpacing:'0.02em'}}>{n}</span>
            ))}
          </div>
          <ul style={{listStyle:'none',margin:0,padding:0}}>
            {R.map((r,i)=>(
              <li key={i} style={{display:'flex',gap:'10px',padding:'10px 0',borderBottom: i<R.length-1?`1px dashed ${SP.line}`:'none'}}>
                <span style={{color:SP.subtle,fontFamily:MONO,fontSize:'11px',fontWeight:700,flexShrink:0}}>—</span>
                <span style={{fontSize:'13.5px',lineHeight:1.5,color:SP.muted}}>{r[0]}</span>
              </li>
            ))}
          </ul>
        </div>
        <div style={{padding: m?'28px 24px':'40px 36px',position:'relative',overflow:'hidden'}}>
          <div style={{position:'absolute',top:'-40%',right:'-15%',width:'360px',height:'360px',background:'radial-gradient(circle,rgba(124,58,237,0.12) 0%,transparent 65%)',pointerEvents:'none'}}/>
          <div style={{position:'relative',display:'inline-flex',alignItems:'center',gap:'8px',marginBottom:'18px',padding:'5px 11px',borderRadius:'999px',background:SP.accentTint,border:`1px solid ${SP.accentTintB}`,fontFamily:MONO,fontSize:'11px',letterSpacing:'0.22em',color:SP.accentDeep,fontWeight:700,textTransform:'uppercase'}}>After DAGGPT</div>
          <div style={{position:'relative',fontFamily:SERIF,fontSize:'clamp(28px,3.4vw,40px)',lineHeight:1.05,letterSpacing:'-0.028em',color:SP.ink,fontWeight:400,marginBottom:'22px'}}>
            One vendor, every model, one invoice. <em style={{fontStyle:'italic',background:SP.accentGrad,WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',display:'inline-block',paddingRight:'0.12em',paddingBottom:'0.08em'}}>No switchboard.</em>
          </div>
          <div style={{position:'relative',display:'inline-flex',alignItems:'center',gap:'12px',marginBottom:'26px',padding:'12px 18px',borderRadius:'14px',background:'rgba(255,255,255,0.05)',border:`1px solid ${SP.accentTintB}`,boxShadow:'0 10px 24px -14px rgba(124,58,237,0.28)'}}>
            <motion.span animate={{scale:[1,1.3,1],opacity:[1,0.5,1]}} transition={{duration:2,repeat:Infinity,ease:'easeInOut'}} style={{width:'8px',height:'8px',borderRadius:'50%',background:SP.accentGrad}}/>
            <span style={{fontFamily:SERIF,fontSize:'17px',color:SP.ink,fontWeight:500,letterSpacing:'-0.018em'}}>DAGGPT</span>
            <span style={{fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.2em',color:SP.muted,fontWeight:600}}>· 400+ models</span>
          </div>
          <ul style={{position:'relative',listStyle:'none',margin:0,padding:0}}>
            {R.map((r,i)=>(
              <li key={i} style={{display:'flex',gap:'10px',padding:'10px 0',borderBottom: i<R.length-1?`1px dashed ${SP.lineSoft}`:'none'}}>
                <span style={{color:SP.accentA,fontFamily:MONO,fontSize:'12px',fontWeight:700,flexShrink:0}}>✓</span>
                <span style={{fontSize:'13.5px',lineHeight:1.5,color:SP.ink,fontWeight:500}}>{r[1]}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ═══ 9 · USE CASES ═══ */
function UseCases(){
  const {m,t} = useBp();
  const C = [
    {k:'01',icon:FlaskConical,name:'R&D teams',job:'Compare frontier models, generate scaffolding, document internals without vendor lock-in.',pts:['Route Claude for reasoning, DeepSeek for throughput, Gemini for long-context','Shared prompt library across the team','Zero-training promise protects your proprietary code']},
    {k:'02',icon:Megaphone,name:'Marketing at scale',job:'Brand-safe image generation, video campaigns, copy variants — all metered and reviewable.',pts:['Workspace-scoped brand guidelines','Per-project billing for campaign reconciliation','Generation history for legal review']},
    {k:'03',icon:Scale,name:'Legal & compliance',job:'Contract review, policy drafting, research with a paper trail.',pts:['Zero-training promise — client data stays client data','Workspace isolation per matter','Audit logs on the roadmap for chain-of-custody']},
    {k:'04',icon:Briefcase,name:'Agencies & consultancies',job:'Deliver AI work to clients without reselling someone else\'s subscription.',pts:['Per-client workspaces with white-glove isolation','One bill for you, clean pass-through for clients','400+ models means you pick the right tool, every time']},
  ];
  return (
    <section style={{position:'relative',zIndex:1,maxWidth:'1400px',margin:'0 auto',padding: m?'0 20px 88px':t?'0 32px 110px':'0 48px 140px'}}>
      <SectionHead icon={Users2} eyebrow="Who buys this" title="Built for teams that can't afford" em="a leak" tail=" — or another vendor." tagline="Four starting points we see often. Your shape may vary — we'll scope it with you on a 30-minute call."/>
      <div style={{display:'grid',gridTemplateColumns: m?'1fr':'repeat(2,1fr)',gap: m?'14px':'20px'}}>
        {C.map((c,i)=>{
          const I = c.icon;
          return (
            <motion.div key={c.k} initial={{opacity:0,y:24}} whileInView={{opacity:1,y:0}} viewport={{once:true,amount:0.2}} transition={{duration:0.7,delay:i*0.08,ease:[0.22,1,0.36,1]}}
              style={{position:'relative',background:'rgba(255,255,255,0.03)',backdropFilter:'blur(20px)',border:`1px solid ${SP.line}`,borderRadius:'24px',padding: m?'26px 24px':'32px 32px 30px',overflow:'hidden',boxShadow:'0 1px 2px rgba(0,0,0,0.2),0 22px 56px -40px rgba(124,58,237,0.16)'}}>
              <div style={{position:'absolute',right:'-10%',bottom:'-30%',fontFamily:SERIF,fontSize:'240px',lineHeight:1,fontWeight:400,color:'rgba(124,58,237,0.05)',pointerEvents:'none',userSelect:'none'}}>{c.k}</div>
              <div style={{position:'relative',display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'18px'}}>
                <div style={{fontFamily:MONO,fontSize:'12px',letterSpacing:'0.22em',color:SP.accentA,fontWeight:700}}>{c.k}</div>
                <div style={{width:'40px',height:'40px',borderRadius:'12px',background:SP.accentTint,border:`1px solid ${SP.accentTintB}`,display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <I size={18} color={SP.accentA} strokeWidth={1.8}/>
                </div>
              </div>
              <div style={{position:'relative',fontFamily:SERIF,fontSize:'28px',lineHeight:1.08,letterSpacing:'-0.025em',color:SP.ink,fontWeight:400,marginBottom:'10px'}}>{c.name}</div>
              <div style={{position:'relative',fontFamily:SERIF,fontStyle:'italic',fontSize:'15.5px',lineHeight:1.5,color:SP.muted,marginBottom:'20px'}}>
                <span style={{fontFamily:MONO,fontStyle:'normal',fontSize:'11.5px',letterSpacing:'0.22em',color:SP.accentDeep,fontWeight:700,marginRight:'8px'}}>The job —</span>{c.job}
              </div>
              <ul style={{position:'relative',listStyle:'none',margin:0,padding:0}}>
                {c.pts.map(p=>(
                  <li key={p} style={{display:'flex',gap:'10px',padding:'8px 0',fontSize:'14px',lineHeight:1.55,color:SP.inkSoft}}>
                    <span style={{color:SP.accentA,fontFamily:MONO,fontSize:'11px',fontWeight:700,flexShrink:0,paddingTop:'2px'}}>✓</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

/* ═══ 10 · SWITCHING ═══ */
function Switching(){
  const {m,t} = useBp();
  const S = [
    {k:'01',title:'Bring your identity',body:'Use the identity provider you already trust. Same groups, same policies, the login your team already has.'},
    {k:'02',title:'Import your prompts',body:'Bring prompt libraries as JSON, Markdown, or share links. We parse the common export formats from the big three.'},
    {k:'03',title:'Run parallel',       body:'Keep your old vendor for 30 days. Run a pilot team on DAGGPT. Compare receipts. Cut over when you\'re ready.'},
  ];
  return (
    <section style={{position:'relative',zIndex:1,maxWidth:'1400px',margin:'0 auto',padding: m?'0 20px 88px':t?'0 32px 110px':'0 48px 140px'}}>
      <SectionHead eyebrow="Coming from somewhere else?" title="OpenAI. Claude. Gemini." em="Move in a weekend" tagline="The SDK speaks OpenAI-compatible REST. Most migrations are a base-URL change, an API key swap, and a parallel pilot."/>
      <div style={{position:'relative',display:'grid',gridTemplateColumns: m?'1fr':'repeat(3,1fr)',background:'rgba(255,255,255,0.02)',backdropFilter:'blur(20px)',border:`1px solid ${SP.line}`,borderRadius:'26px',overflow:'hidden'}}>
        {!m && <div aria-hidden style={{position:'absolute',top:'68px',left:'12%',right:'12%',height:'1px',background:`repeating-linear-gradient(to right,${SP.accentTintB} 0 6px,transparent 6px 12px)`,pointerEvents:'none'}}/>}
        {S.map((s,i)=>(
          <motion.div key={s.k} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true,amount:0.3}} transition={{duration:0.6,delay:i*0.1}}
            style={{position:'relative',padding: m?'26px 24px':'34px 32px 36px',borderRight: !m && i<S.length-1?`1px solid ${SP.line}`:'none',borderBottom: m && i<S.length-1?`1px solid ${SP.line}`:'none'}}>
            <div style={{position:'relative',display:'flex',alignItems:'center',gap:'14px',marginBottom:'18px'}}>
              <div style={{width:'38px',height:'38px',borderRadius:'50%',background:'rgba(255,255,255,0.05)',border:`1.5px solid ${SP.accentTintB}`,display:'flex',alignItems:'center',justifyContent:'center',fontFamily:MONO,fontSize:'12px',fontWeight:700,letterSpacing:'0.08em',color:SP.accentA,zIndex:1}}>{s.k}</div>
              <span style={{fontFamily:MONO,fontSize:'11px',letterSpacing:'0.26em',color:SP.muted,fontWeight:700,textTransform:'uppercase'}}>Step</span>
            </div>
            <div style={{fontFamily:SERIF,fontSize:'26px',lineHeight:1.1,letterSpacing:'-0.025em',color:SP.ink,fontWeight:400,marginBottom:'12px'}}>{s.title}</div>
            <div style={{fontSize:'14px',lineHeight:1.55,color:SP.muted}}>{s.body}</div>
          </motion.div>
        ))}
      </div>
      <div style={{marginTop: m?'24px':'32px',padding: m?'20px 22px':'24px 28px',border:`1px solid ${SP.accentTintB}`,borderRadius:'18px',background:SP.accentTint,display:'flex',gap:'16px',alignItems:'center',flexWrap:'wrap'}}>
        <Terminal size={20} color={SP.accentA} strokeWidth={1.8}/>
        <p style={{margin:0,flex:1,minWidth:'260px',fontFamily:SERIF,fontStyle:'italic',fontSize:'15px',lineHeight:1.55,color:SP.inkSoft}}>
          Our REST API speaks the OpenAI format for <span style={{fontFamily:MONO,fontStyle:'normal',fontSize:'13px',color:SP.accentDeep,fontWeight:600}}>chat.completions</span> — most SDKs work with a base-URL change and an API key swap.
        </p>
      </div>
    </section>
  );
}

/* ═══ 11 · TOPOLOGY ═══ */
function Topology(){
  const {m,t} = useBp();
  const MODES = [
    {tag:'01',name:'DAGGPT Cloud',status:'live',head:'The fastest path',body:'All 400+ models, shared infra, your tenant logically isolated. SSO and team workspaces available today.',rows:[['Tenancy','Logical isolation'],['Models','400+ · Chat · Image · Video · Audio'],['Access','SSO · Workspaces · Unified billing']]},
    {tag:'02',name:'Private cloud',status:'roadmap',head:'Your infra, our control plane',body:'Your AWS, GCP, or Azure account. Your VPC. Your KMS keys. Our control plane. On the roadmap — contact sales for scoping.',rows:[['Tenancy','Single-tenant · planned'],['Keys','BYOK · planned'],['Status','Scoping']],spotlight:true},
    {tag:'03',name:'Air-gapped',status:'by-request',head:'For regulated sectors',body:'For environments that cannot connect outbound. Deeply custom — by request only. We\'ll scope feasibility together.',rows:[['Tenancy','On-premise'],['Egress','None'],['Status','By request']]},
  ];
  return (
    <section style={{position:'relative',zIndex:1,maxWidth:'1400px',margin:'0 auto',padding: m?'0 20px 88px':t?'0 32px 110px':'0 48px 140px'}}>
      <SectionHead icon={Globe2} eyebrow="Deployment topology" title="Same product." em="Three postures" tagline="Start in our cloud. Move to yours when compliance asks. Air-gap it when they insist."/>
      <div style={{display:'grid',gridTemplateColumns: m?'1fr':t?'1fr':'repeat(3,1fr)',gap: m?'14px':'18px'}}>
        {MODES.map((x,i)=>{
          const spot = x.spotlight;
          return (
            <motion.div key={x.tag} initial={{opacity:0,y:24}} whileInView={{opacity:1,y:0}} viewport={{once:true,amount:0.2}} transition={{duration:0.7,delay:i*0.1,ease:[0.22,1,0.36,1]}}
              style={{position:'relative',borderRadius:'24px',overflow:'hidden',padding: m?'26px 24px 24px':'32px 28px 28px',display:'flex',flexDirection:'column',gap:'16px',
                background: spot?'linear-gradient(160deg,#0A0A1A 0%,#14112E 60%,#0A0A1A 100%)':'rgba(255,255,255,0.03)', backdropFilter:'blur(20px)',
                border: spot?'1px solid rgba(167,139,250,0.22)':`1px solid ${SP.line}`,
                boxShadow: spot?'0 40px 100px -50px rgba(124,58,237,0.45)':'0 1px 2px rgba(10,10,10,0.03),0 24px 60px -40px rgba(124,58,237,0.14)'}}>
              {spot && <div style={{position:'absolute',top:'-30%',right:'-15%',width:'360px',height:'360px',background:'radial-gradient(circle,rgba(124,58,237,0.35) 0%,transparent 60%)',filter:'blur(20px)',pointerEvents:'none'}}/>}
              <div style={{position:'relative',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <div style={{fontFamily:MONO,fontSize:'12px',letterSpacing:'0.22em',color:spot?SP.accentC:SP.accentA,fontWeight:700}}>{x.tag}</div>
                <StatusPill status={x.status}/>
              </div>
              <Diag kind={x.tag} spot={!!spot}/>
              <div>
                <div style={{fontFamily:SERIF,fontSize:'26px',lineHeight:1.08,letterSpacing:'-0.025em',color:spot?'#FFF':SP.ink,fontWeight:400,marginBottom:'6px'}}>{x.name}</div>
                <div style={{fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.22em',color:spot?SP.accentC:SP.accentDeep,fontWeight:700,textTransform:'uppercase',marginBottom:'10px'}}>{x.head}</div>
                <div style={{fontSize:'13.5px',lineHeight:1.55,color:spot?'rgba(255,255,255,0.72)':SP.muted}}>{x.body}</div>
              </div>
              <div style={{marginTop:'auto',borderTop: spot?'1px solid rgba(255,255,255,0.08)':`1px solid ${SP.lineSoft}`,paddingTop:'14px',display:'flex',flexDirection:'column',gap:'8px'}}>
                {x.rows.map(([k,v])=>(
                  <div key={k} style={{display:'flex',justifyContent:'space-between',gap:'12px',fontFamily:MONO,fontSize:'11px',letterSpacing:'0.06em'}}>
                    <span style={{color:spot?'rgba(196,181,253,0.8)':SP.muted,fontWeight:600,textTransform:'uppercase',letterSpacing:'0.18em',fontSize:'11px'}}>{k}</span>
                    <span style={{color:spot?'#FFF':SP.ink,fontWeight:500,textAlign:'right'}}>{v}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

/* ── Topology Diagram · perimeter-aware SVG ── */
function Diag({kind,spot}){
  const accent  = spot ? '#C4B5FD' : SP.accentA;
  const line    = spot ? 'rgba(196,181,253,0.35)' : 'rgba(124,58,237,0.25)';
  const fill    = spot ? 'rgba(20,17,46,0.6)' : 'rgba(255,255,255,0.05)';
  const txt     = spot ? 'rgba(255,255,255,0.9)' : SP.ink;
  const dim     = spot ? 'rgba(196,181,253,0.6)' : SP.muted;
  const bg      = spot ? 'rgba(10,10,25,0.5)' : SP.canvasCool;
  const border  = spot ? '1px solid rgba(167,139,250,0.22)' : `1px solid ${SP.lineSoft}`;
  const uid     = `t${kind}`;

  return (
    <div style={{position:'relative',borderRadius:'14px',background:bg,border,overflow:'hidden'}}>
      <svg viewBox="0 0 360 180" style={{width:'100%',height:'160px',display:'block'}}>
        <defs>
          <pattern id={`grid-${uid}`} width="18" height="18" patternUnits="userSpaceOnUse">
            <path d="M 18 0 L 0 0 0 18" fill="none" stroke={line} strokeWidth="0.4" opacity="0.35"/>
          </pattern>
          <linearGradient id={`flow-${uid}`} x1="0" x2="1">
            <stop offset="0%" stopColor={accent} stopOpacity="0.15"/>
            <stop offset="50%" stopColor={accent} stopOpacity="0.9"/>
            <stop offset="100%" stopColor={accent} stopOpacity="0.15"/>
          </linearGradient>
          <filter id={`glow-${uid}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2"/>
          </filter>
        </defs>
        <rect width="360" height="180" fill={`url(#grid-${uid})`}/>

        {kind === '01' && (
          <>
            <g opacity="0.6">
              <rect x="60" y="30" width="240" height="120" rx="18" fill="none" stroke={accent} strokeWidth="1" strokeDasharray="2 4"/>
              <text x="72" y="48" fontFamily={MONO} fontSize="8" fontWeight="700" fill={dim} letterSpacing="0.16em">SHARED CLOUD</text>
            </g>
            {[{x:90,y:70,l:'tenant'},{x:90,y:110,l:'tenant'}].map((r,i)=>(
              <g key={i} opacity="0.5">
                <rect x={r.x} y={r.y} width="56" height="26" rx="6" fill="none" stroke={accent} strokeWidth="0.8"/>
                <text x={r.x+28} y={r.y+17} textAnchor="middle" fontFamily={MONO} fontSize="8" fill={dim}>{r.l}</text>
              </g>
            ))}
            <g>
              <rect x="90" y="90" width="56" height="26" rx="6" fill={fill} stroke={accent} strokeWidth="1.4"/>
              <rect x="90" y="90" width="56" height="26" rx="6" fill="none" stroke={accent} strokeWidth="1.4" filter={`url(#glow-${uid})`} opacity="0.5"/>
              <text x="118" y="107" textAnchor="middle" fontFamily={MONO} fontSize="9" fontWeight="700" fill={txt}>YOU</text>
            </g>
            <g>
              <rect x="180" y="80" width="64" height="46" rx="8" fill={fill} stroke={accent} strokeWidth="1.4"/>
              <text x="212" y="102" textAnchor="middle" fontFamily={SERIF} fontSize="14" fontWeight="500" fill={txt}>daggpt</text>
              <text x="212" y="116" textAnchor="middle" fontFamily={MONO} fontSize="7" fill={dim} letterSpacing="0.12em">CONTROL PLANE</text>
            </g>
            <path d={`M 146 103 L 180 103`} stroke={`url(#flow-${uid})`} strokeWidth="1.8"/>
            {[{x:280,y:62,l:'GPT'},{x:280,y:94,l:'Claude'},{x:280,y:126,l:'Imagen'}].map((r,i)=>(
              <g key={r.l}>
                <rect x={r.x} y={r.y} width="54" height="22" rx="5" fill={fill} stroke={accent} strokeWidth="1"/>
                <text x={r.x+27} y={r.y+14} textAnchor="middle" fontFamily={MONO} fontSize="8" fontWeight="700" fill={txt}>{r.l}</text>
                <path d={`M 244 103 Q 262 103 ${r.x} ${r.y+11}`} fill="none" stroke={accent} strokeOpacity="0.45" strokeWidth="0.9" strokeDasharray="2 2"/>
              </g>
            ))}
            <motion.circle r="2.4" fill={accent} filter={`url(#glow-${uid})`}
              animate={{offsetDistance:['0%','100%']}}
              transition={{duration:2.6,repeat:Infinity,ease:'linear'}}
              style={{offsetPath:`path('M 146 103 L 244 103')`}}/>
          </>
        )}

        {kind === '02' && (
          <>
            <g>
              <rect x="40" y="22" width="280" height="136" rx="14" fill="none" stroke={accent} strokeWidth="2"/>
              <rect x="40" y="22" width="280" height="136" rx="14" fill="none" stroke={accent} strokeWidth="2" strokeDasharray="5 3" opacity="0.5"/>
              <rect x="40" y="22" width="90" height="18" rx="4" fill={accent}/>
              <text x="52" y="35" fontFamily={MONO} fontSize="9" fontWeight="700" fill="#14112E" letterSpacing="0.18em">YOUR VPC</text>
            </g>
            <g>
              <rect x="64" y="72" width="60" height="30" rx="6" fill={fill} stroke={accent} strokeWidth="1.4"/>
              <text x="94" y="91" textAnchor="middle" fontFamily={MONO} fontSize="9" fontWeight="700" fill={txt}>YOU</text>
            </g>
            <g>
              <rect x="154" y="62" width="68" height="50" rx="8" fill={fill} stroke={accent} strokeWidth="1.6"/>
              <rect x="154" y="62" width="68" height="50" rx="8" fill="none" stroke={accent} strokeWidth="1.6" filter={`url(#glow-${uid})`} opacity="0.55"/>
              <text x="188" y="87" textAnchor="middle" fontFamily={SERIF} fontSize="15" fontWeight="500" fill={txt}>daggpt</text>
              <text x="188" y="101" textAnchor="middle" fontFamily={MONO} fontSize="7" fill={dim} letterSpacing="0.1em">IN YOUR VPC</text>
            </g>
            <g>
              <rect x="252" y="48" width="56" height="22" rx="5" fill={fill} stroke={accent} strokeWidth="1"/>
              <text x="280" y="62" textAnchor="middle" fontFamily={MONO} fontSize="8" fontWeight="700" fill={txt}>KMS</text>
              <rect x="252" y="78" width="56" height="22" rx="5" fill={fill} stroke={accent} strokeWidth="1"/>
              <text x="280" y="92" textAnchor="middle" fontFamily={MONO} fontSize="8" fontWeight="700" fill={txt}>Models</text>
              <rect x="252" y="108" width="56" height="22" rx="5" fill={fill} stroke={accent} strokeWidth="1"/>
              <text x="280" y="122" textAnchor="middle" fontFamily={MONO} fontSize="8" fontWeight="700" fill={txt}>Logs</text>
            </g>
            <path d={`M 124 87 L 154 87`} stroke={`url(#flow-${uid})`} strokeWidth="2"/>
            <path d={`M 222 80 L 252 59`} stroke={accent} strokeOpacity="0.5" strokeWidth="0.9" strokeDasharray="2 2"/>
            <path d={`M 222 87 L 252 89`} stroke={accent} strokeOpacity="0.5" strokeWidth="0.9" strokeDasharray="2 2"/>
            <path d={`M 222 96 L 252 119`} stroke={accent} strokeOpacity="0.5" strokeWidth="0.9" strokeDasharray="2 2"/>
            <motion.circle r="2.6" fill={accent} filter={`url(#glow-${uid})`}
              animate={{cx:[124,188,124],cy:[87,87,87],opacity:[1,1,0.3]}}
              transition={{duration:2.2,repeat:Infinity,ease:'easeInOut'}}/>
          </>
        )}

        {kind === '03' && (
          <>
            <g>
              <rect x="40" y="22" width="280" height="136" rx="14" fill="none" stroke={accent} strokeWidth="2"/>
              <rect x="40" y="22" width="112" height="18" rx="4" fill={accent}/>
              <text x="52" y="35" fontFamily={MONO} fontSize="9" fontWeight="700" fill="#14112E" letterSpacing="0.18em">AIR-GAPPED</text>
            </g>
            <g>
              <rect x="60" y="72" width="60" height="30" rx="6" fill={fill} stroke={accent} strokeWidth="1.4"/>
              <text x="90" y="91" textAnchor="middle" fontFamily={MONO} fontSize="9" fontWeight="700" fill={txt}>YOU</text>
            </g>
            <g>
              <rect x="152" y="62" width="68" height="50" rx="8" fill={fill} stroke={accent} strokeWidth="1.6"/>
              <text x="186" y="87" textAnchor="middle" fontFamily={SERIF} fontSize="15" fontWeight="500" fill={txt}>daggpt</text>
              <text x="186" y="101" textAnchor="middle" fontFamily={MONO} fontSize="7" fill={dim} letterSpacing="0.1em">ON-PREMISE</text>
            </g>
            <g>
              <rect x="244" y="72" width="62" height="30" rx="6" fill={fill} stroke={accent} strokeWidth="1.4"/>
              <text x="275" y="91" textAnchor="middle" fontFamily={MONO} fontSize="9" fontWeight="700" fill={txt}>Models</text>
            </g>
            <path d={`M 120 87 L 152 87`} stroke={accent} strokeWidth="2"/>
            <path d={`M 220 87 L 244 87`} stroke={accent} strokeWidth="2"/>
            {/* no-egress boundary slash */}
            <g opacity="0.75">
              <line x1="320" y1="36" x2="354" y2="2" stroke="#F87171" strokeWidth="1.6" strokeDasharray="3 2"/>
              <line x1="320" y1="148" x2="354" y2="182" stroke="#F87171" strokeWidth="1.6" strokeDasharray="3 2"/>
              <text x="332" y="165" fontFamily={MONO} fontSize="7.5" fontWeight="700" fill="#F87171" letterSpacing="0.14em">NO EGRESS</text>
            </g>
            <motion.circle r="2.4" fill={accent}
              animate={{cx:[120,186,244,186,120],cy:[87,87,87,87,87]}}
              transition={{duration:3,repeat:Infinity,ease:'easeInOut'}}/>
          </>
        )}
      </svg>
    </div>
  );
}

/* ── DataFlow Diagram · editorial-grade animated pipeline ── */
function DataFlowDiagram({mobile}){
  const NODES = [
    {x:100, l:'Your prompt',   s:'TLS 1.3'},
    {x:300, l:'Edge gateway',  s:'Auth · rate-limit'},
    {x:500, l:'DAGGPT proxy',  s:'In-flight only'},
    {x:700, l:'Model provider',s:'Zero-retention'},
    {x:900, l:'Purge',         s:'< 24h on delete'},
  ];
  const ICONS = {
    0:(cx,cy)=> <g><circle cx={cx} cy={cy-2} r="5" fill="none" stroke="#E9D5FF" strokeWidth="1.2"/><rect x={cx-6} y={cy+4} width="12" height="7" rx="3" fill="none" stroke="#E9D5FF" strokeWidth="1.2"/></g>,
    1:(cx,cy)=> <g><path d={`M ${cx-7} ${cy-6} L ${cx+7} ${cy-6} L ${cx+7} ${cy+4} Q ${cx+7} ${cy+9} ${cx} ${cy+10} Q ${cx-7} ${cy+9} ${cx-7} ${cy+4} Z`} fill="none" stroke="#E9D5FF" strokeWidth="1.2"/></g>,
    2:(cx,cy)=> <g><rect x={cx-8} y={cy-7} width="16" height="14" rx="3" fill="none" stroke="#E9D5FF" strokeWidth="1.2"/><circle cx={cx-3} cy={cy} r="1.6" fill="#E9D5FF"/><circle cx={cx+3} cy={cy} r="1.6" fill="#E9D5FF"/></g>,
    3:(cx,cy)=> <g><polygon points={`${cx-8},${cy-5} ${cx},${cy-9} ${cx+8},${cy-5} ${cx+8},${cy+5} ${cx},${cy+9} ${cx-8},${cy+5}`} fill="none" stroke="#E9D5FF" strokeWidth="1.2"/><circle cx={cx} cy={cy} r="2.2" fill="#E9D5FF"/></g>,
    4:(cx,cy)=> <g><rect x={cx-7} y={cy-4} width="14" height="12" rx="1.5" fill="none" stroke="#E9D5FF" strokeWidth="1.2"/><line x1={cx-9} y1={cy-4} x2={cx+9} y2={cy-4} stroke="#E9D5FF" strokeWidth="1.4"/><line x1={cx-3} y1={cy-7} x2={cx+3} y2={cy-7} stroke="#E9D5FF" strokeWidth="1.2"/></g>,
  };
  return (
    <div style={{position:'relative',marginBottom:'52px',padding: mobile?'22px 8px':'36px 28px 28px',border:'1px solid rgba(167,139,250,0.2)',borderRadius:'22px',background:'linear-gradient(180deg,rgba(124,58,237,0.06) 0%,rgba(10,10,25,0.55) 100%)',overflow:'hidden'}}>
      <div style={{position:'absolute',inset:0,backgroundImage:'radial-gradient(circle at 50% 50%,rgba(167,139,250,0.1) 0%,transparent 60%)',pointerEvents:'none'}}/>
      <svg viewBox="0 0 1000 260" style={{width:'100%',height:'auto',display:'block',position:'relative'}}>
        <defs>
          <pattern id="dfgrid" width="24" height="24" patternUnits="userSpaceOnUse">
            <path d="M 24 0 L 0 0 0 24" fill="none" stroke="rgba(167,139,250,0.12)" strokeWidth="0.5"/>
          </pattern>
          <linearGradient id="dfbeam" x1="0" x2="1">
            <stop offset="0%"  stopColor="#7C3AED" stopOpacity="0"/>
            <stop offset="18%" stopColor="#A78BFA" stopOpacity="0.85"/>
            <stop offset="82%" stopColor="#C4B5FD" stopOpacity="0.85"/>
            <stop offset="100%" stopColor="#E9D5FF" stopOpacity="0"/>
          </linearGradient>
          <radialGradient id="dfhalo" cx="50%" cy="50%" r="50%">
            <stop offset="0%"  stopColor="#A78BFA" stopOpacity="0.6"/>
            <stop offset="60%" stopColor="#A78BFA" stopOpacity="0"/>
          </radialGradient>
          <filter id="dfglow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3"/>
          </filter>
        </defs>
        <rect width="1000" height="260" fill="url(#dfgrid)" opacity="0.5"/>
        {/* connection rails */}
        <line x1="100" y1="130" x2="900" y2="130" stroke="rgba(167,139,250,0.28)" strokeWidth="1" strokeDasharray="4 6"/>
        <rect x="100" y="129" width="800" height="2" fill="url(#dfbeam)"/>
        {/* travelling packets */}
        {[0,1,2].map(i=>(
          <motion.circle key={i} r="4" fill="#E9D5FF" filter="url(#dfglow)"
            cy="130"
            animate={{cx:[100,900],opacity:[0,1,1,0]}}
            transition={{duration:4,repeat:Infinity,ease:'linear',delay:i*1.33}}/>
        ))}
        {/* nodes */}
        {NODES.map((n,i)=>(
          <g key={n.l}>
            <circle cx={n.x} cy="130" r="44" fill="url(#dfhalo)" opacity="0.5"/>
            <motion.circle cx={n.x} cy="130" r="36"
              animate={{r:[34,40,34],opacity:[0.2,0.5,0.2]}}
              transition={{duration:3,repeat:Infinity,ease:'easeInOut',delay:i*0.3}}
              fill="none" stroke="#A78BFA" strokeWidth="0.8"/>
            <circle cx={n.x} cy="130" r="28" fill="#0B0B1A" stroke="#A78BFA" strokeWidth="1.4"/>
            {ICONS[i](n.x,130)}
            <text x={n.x} y="78" textAnchor="middle" fontFamily={MONO} fontSize="10" fontWeight="700" fill="rgba(196,181,253,0.85)" letterSpacing="0.22em">{`0${i+1}`}</text>
            <text x={n.x} y="184" textAnchor="middle" fontFamily={SERIF} fontSize="16" fontWeight="500" fill="#FFFFFF" letterSpacing="-0.01em">{n.l}</text>
            <text x={n.x} y="204" textAnchor="middle" fontFamily={MONO} fontSize="10" fill="rgba(196,181,253,0.65)" letterSpacing="0.08em">{n.s}</text>
          </g>
        ))}
      </svg>
      <div style={{position:'relative',display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:'18px',padding:'14px 18px',borderTop:'1px solid rgba(167,139,250,0.14)',flexWrap:'wrap',gap:'10px'}}>
        <span style={{fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.22em',color:'rgba(196,181,253,0.85)',fontWeight:700,textTransform:'uppercase'}}>Live path</span>
        <span style={{fontFamily:MONO,fontSize:'12px',color:'rgba(255,255,255,0.55)'}}>TLS&nbsp;1.3&nbsp;·&nbsp;AES-256&nbsp;·&nbsp;No&nbsp;plaintext&nbsp;logs&nbsp;·&nbsp;24h&nbsp;purge</span>
        <span style={{display:'inline-flex',alignItems:'center',gap:'6px',fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.2em',color:'#34D399',fontWeight:700,textTransform:'uppercase'}}>
          <motion.span animate={{scale:[1,1.4,1],opacity:[1,0.4,1]}} transition={{duration:1.8,repeat:Infinity,ease:'easeInOut'}} style={{width:'7px',height:'7px',borderRadius:'50%',background:'#10B981'}}/>
          In-flight only
        </span>
      </div>
    </div>
  );
}

/* ═══ · DAG AI MODELS (flagship roadmap) ═══ */
function DagAiModels(){
  const {m,t} = useBp();
  const FAMILIES = [
    {
      k:'01', tag:'DAG TEXT', name:'Frontier reasoning, built for enterprise.', icon:Brain,
      blurb:'Our own large-language stack. Privacy-first pretraining, long-context by default, domain-tuned variants for the work your team actually ships.',
      caps:[
        {i:MessageCircle, l:'Multi-turn reasoning at frontier quality'},
        {i:InfinityIcon,  l:'Million-token context window'},
        {i:ShieldCheck,   l:'No public-data contamination from customer workloads'},
        {i:FlaskConical,  l:'Domain-tuned variants · legal · clinical · code'},
        {i:Zap,           l:'Tool-use, function-calling, agent primitives'},
        {i:Code2,         l:'Enterprise memory you actually control'},
      ],
    },
    {
      k:'02', tag:'DAG VISION', name:'Generate, detect, and understand images.', icon:Eye,
      blurb:'Image generation and perception in one model family. Brand-safe output, pixel-accurate detection, and edit flows that respect your compositing pipeline.',
      caps:[
        {i:Sparkles,   l:'Photoreal generation with brand-token tuning'},
        {i:ScanSearch, l:'Native object detection & segmentation'},
        {i:Eye,        l:'OCR, document understanding, diagram parsing'},
        {i:Wand2,      l:'In-paint, out-paint, precision edits'},
        {i:Layers,     l:'Layered output · alpha, depth, surface maps'},
        {i:Cpu,        l:'On-device inference for regulated workloads'},
      ],
    },
    {
      k:'03', tag:'DAG MOTION', name:'Video with director-level control.', icon:Film,
      blurb:'Text-to-video at narrative length. Camera paths you actually script, character consistency across shots, and audio that lines up with the frame.',
      caps:[
        {i:Film,          l:'Narrative-length generation, not just clips'},
        {i:MousePointer2, l:'Frame-accurate motion & keyframe control'},
        {i:Move3d,        l:'Camera scripting · pan, dolly, orbit, zoom'},
        {i:Users2,        l:'Character consistency across shots & scenes'},
        {i:Music2,        l:'Synchronized dialogue, score, and SFX'},
        {i:Wand2,         l:'Inpainting & relight on existing footage'},
      ],
    },
  ];
  const MORE = [
    {i:Music2, k:'DAG Audio',  s:'Voice, score, SFX — one model'},
    {i:Code2,  k:'DAG Code',   s:'IDE-native completion & review'},
    {i:Layers, k:'DAG Embed',  s:'Retrieval that stays private'},
    {i:Cpu,    k:'DAG Edge',   s:'Small, fast, fine-tunable'},
  ];
  return (
    <section style={{position:'relative',zIndex:1,
      margin: m?'0 16px 88px':t?'0 24px 110px':'0 48px 140px',
      borderRadius: m?'28px':'36px',overflow:'hidden',
      background:'rgba(255,255,255,0.03)',backdropFilter:'blur(20px)',WebkitBackdropFilter:'blur(20px)',
      boxShadow:'0 80px 180px -60px rgba(124,58,237,0.55),inset 0 1px 0 rgba(255,255,255,0.06)',
      border:'1px solid rgba(167,139,250,0.2)'}}>

      {/* Ambient glow */}
      <div aria-hidden style={{position:'absolute',top:'-20%',left:'-10%',width:'620px',height:'620px',background:'radial-gradient(circle,rgba(124,58,237,0.45) 0%,transparent 60%)',filter:'blur(60px)',pointerEvents:'none'}}/>
      <div aria-hidden style={{position:'absolute',bottom:'-30%',right:'-10%',width:'720px',height:'720px',background:'radial-gradient(circle,rgba(167,139,250,0.25) 0%,transparent 60%)',filter:'blur(60px)',pointerEvents:'none'}}/>

      <div style={{position:'relative',padding: m?'48px 22px 52px':t?'64px 40px':'88px 72px'}}>
        {/* Header */}
        <div style={{display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap',marginBottom:'22px'}}>
          <span style={{display:'inline-flex',alignItems:'center',gap:'8px',padding:'6px 14px',borderRadius:'999px',background:'rgba(167,139,250,0.14)',border:'1px solid rgba(167,139,250,0.35)',fontFamily:MONO,fontSize:'12px',letterSpacing:'0.24em',color:'#E9D5FF',fontWeight:700,textTransform:'uppercase'}}>
            <Sparkles size={12} strokeWidth={2}/> Coming · Built by us
          </span>
          <span style={{fontFamily:MONO,fontSize:'12px',letterSpacing:'0.22em',color:'rgba(196,181,253,0.7)',fontWeight:600,textTransform:'uppercase'}}>The DAG AI roster</span>
        </div>

        <div style={{display:'grid',gridTemplateColumns: m||t?'1fr':'1.35fr 1fr',gap: m?'24px':'48px',alignItems:'end',marginBottom: m?'48px':'64px',paddingBottom: m?'32px':'40px',borderBottom:'1px solid rgba(167,139,250,0.16)'}}>
          <h2 style={{fontFamily:SERIF,fontSize:'clamp(40px,6vw,88px)',lineHeight:1,letterSpacing:'-0.04em',color:'#FFFFFF',fontWeight:400,margin:0}}>
            Everyone else's models are today.{' '}
            <em style={{fontStyle:'italic',background:'linear-gradient(135deg,#C4B5FD 0%,#F0ABFC 100%)',WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',display:'inline-block',paddingRight:'0.12em',paddingBottom:'0.08em'}}>Ours are next.</em>
          </h2>
          <p style={{fontFamily:SERIF,fontSize:m?'17px':'19px',lineHeight:1.5,color:'rgba(255,255,255,0.72)',fontStyle:'italic',margin:0,maxWidth:'420px'}}>
            A frontier text, vision, and video stack we're building ourselves. Private by design, enterprise-first, trained without customer content. On the roadmap — early access for existing enterprise customers.
          </p>
        </div>

        {/* Family cards */}
        <div style={{display:'flex',flexDirection:'column',gap: m?'18px':'22px'}}>
          {FAMILIES.map((f,i)=>{
            const I = f.icon;
            return (
              <motion.article key={f.k}
                initial={{opacity:0,y:36}} whileInView={{opacity:1,y:0}}
                viewport={{once:true,amount:0.2}}
                transition={{duration:0.8,delay:i*0.1,ease:[0.22,1,0.36,1]}}
                style={{position:'relative',borderRadius: m?'22px':'26px',overflow:'hidden',
                  background:'linear-gradient(150deg,rgba(124,58,237,0.18) 0%,rgba(15,12,40,0.6) 45%,rgba(10,6,32,0.85) 100%)',
                  border:'1px solid rgba(167,139,250,0.24)',
                  boxShadow:'0 40px 100px -60px rgba(124,58,237,0.5),inset 0 1px 0 rgba(255,255,255,0.04)',
                  padding: m?'28px 22px 30px':'40px 44px 44px'}}>
                {/* inner glow */}
                <div aria-hidden style={{position:'absolute',top:'-40%',right:'-10%',width:'420px',height:'420px',background:'radial-gradient(circle,rgba(167,139,250,0.35) 0%,transparent 65%)',filter:'blur(40px)',pointerEvents:'none'}}/>
                {/* big faded number */}
                <div aria-hidden style={{position:'absolute',right:'-1%',bottom:'-22%',fontFamily:SERIF,fontSize: m?'320px':'520px',lineHeight:1,fontWeight:400,letterSpacing:'-0.06em',color:'rgba(196,181,253,0.05)',pointerEvents:'none',userSelect:'none'}}>{f.k}</div>

                <div style={{position:'relative',display:'grid',gridTemplateColumns: m||t?'1fr':'1.15fr 1.4fr',gap: m?'24px':'48px'}}>
                  {/* left: intro */}
                  <div>
                    <div style={{display:'flex',alignItems:'center',gap:'14px',marginBottom:'22px'}}>
                      <div style={{width:m?'56px':'64px',height:m?'56px':'64px',borderRadius:'18px',background:'linear-gradient(135deg,#7C3AED 0%,#C026D3 100%)',display:'flex',alignItems:'center',justifyContent:'center',boxShadow:'0 20px 40px -16px rgba(124,58,237,0.7)'}}>
                        <I size={m?26:30} color="#FFFFFF" strokeWidth={1.6}/>
                      </div>
                      <div>
                        <div style={{fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.28em',color:'#C4B5FD',fontWeight:700,marginBottom:'6px',textTransform:'uppercase'}}>{f.tag}</div>
                        <StatusPill status="roadmap"/>
                      </div>
                    </div>
                    <div style={{fontFamily:SERIF,fontSize: m?'26px':'34px',lineHeight:1.1,letterSpacing:'-0.025em',color:'#FFFFFF',fontWeight:400,marginBottom:'14px'}}>{f.name}</div>
                    <div style={{fontFamily:SERIF,fontStyle:'italic',fontSize: m?'15px':'16px',lineHeight:1.55,color:'rgba(255,255,255,0.72)'}}>{f.blurb}</div>
                  </div>
                  {/* right: capability grid */}
                  <div>
                    <div style={{fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.26em',color:'rgba(196,181,253,0.75)',fontWeight:700,textTransform:'uppercase',marginBottom:'16px'}}>Capabilities on the roadmap</div>
                    <div style={{display:'grid',gridTemplateColumns: m?'1fr':'repeat(2,1fr)',gap:'10px 14px'}}>
                      {f.caps.map(c=>{
                        const CI = c.i;
                        return (
                          <div key={c.l} style={{display:'flex',alignItems:'flex-start',gap:'10px',padding:'12px 14px',borderRadius:'12px',background:'rgba(10,6,32,0.55)',border:'1px solid rgba(167,139,250,0.16)'}}>
                            <CI size={15} color="#C4B5FD" strokeWidth={1.8} style={{flexShrink:0,marginTop:'1px'}}/>
                            <span style={{fontSize:'13px',lineHeight:1.45,color:'rgba(255,255,255,0.86)',fontWeight:400}}>{c.l}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* More on deck */}
        <div style={{marginTop: m?'40px':'56px',paddingTop: m?'28px':'36px',borderTop:'1px solid rgba(167,139,250,0.16)'}}>
          <div style={{display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap',marginBottom:'18px'}}>
            <div style={{fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.24em',color:'rgba(196,181,253,0.85)',fontWeight:700,textTransform:'uppercase'}}>Also on deck</div>
            <span style={{flex:1,height:'1px',background:'rgba(167,139,250,0.18)',minWidth:'40px'}}/>
          </div>
          <div style={{display:'grid',gridTemplateColumns: m?'1fr 1fr':'repeat(4,1fr)',gap: m?'10px':'14px'}}>
            {MORE.map(x=>{
              const XI = x.i;
              return (
                <div key={x.k} style={{padding: m?'16px 14px':'20px 20px',borderRadius:'14px',background:'rgba(10,6,32,0.55)',border:'1px solid rgba(167,139,250,0.18)',display:'flex',flexDirection:'column',gap:'10px'}}>
                  <XI size={18} color="#C4B5FD" strokeWidth={1.8}/>
                  <div>
                    <div style={{fontFamily:SERIF,fontSize:'18px',lineHeight:1.1,color:'#FFFFFF',fontWeight:500,letterSpacing:'-0.018em',marginBottom:'4px'}}>{x.k}</div>
                    <div style={{fontFamily:MONO,fontSize:'12px',letterSpacing:'0.04em',color:'rgba(196,181,253,0.7)',lineHeight:1.5}}>{x.s}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{marginTop:'32px',display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:'16px'}}>
            <p style={{margin:0,fontFamily:SERIF,fontStyle:'italic',fontSize:'16px',lineHeight:1.55,color:'rgba(255,255,255,0.65)',maxWidth:'480px'}}>
              Enterprise customers get early access. Tell us what you'd build — we're steering the roadmap with real workloads.
            </p>
            <a href={MAIL} style={{display:'inline-flex',alignItems:'center',gap:'10px',padding:'14px 28px',borderRadius:'999px',background:'linear-gradient(135deg,#7C3AED 0%,#C026D3 100%)',color:'#FFFFFF',fontFamily:MONO,fontSize:'11px',fontWeight:700,letterSpacing:'0.24em',textTransform:'uppercase',textDecoration:'none',boxShadow:'0 20px 50px -16px rgba(124,58,237,0.8)',transition:'transform 0.2s,box-shadow 0.2s'}}
              onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow='0 28px 60px -18px rgba(124,58,237,1)';}}
              onMouseLeave={e=>{e.currentTarget.style.transform='none';e.currentTarget.style.boxShadow='0 20px 50px -16px rgba(124,58,237,0.8)';}}>
              Request early access <span style={{transform:'translateY(-1px)'}}>→</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══ 12 · PRICING ═══ */
function Pricing(){
  const {m,t} = useBp();
  const INCL = [
    {ok:true, txt:'Enterprise SSO with your existing identity provider'},
    {ok:true, txt:'Team workspaces, per-project billing'},
    {ok:true, txt:'Zero-training promise, 24h purge on deletion'},
    {ok:true, txt:'All 400+ models, one invoice'},
    {ok:true, txt:'Priority support — direct channel to the team'},
            {ok:false,txt:'Custom retention policies'},
    {ok:false,txt:'Private model hosting (VPC)'},
    {ok:false,txt:'Early access to DAG AI models'},
  ];
  return (
    <section id="pricing" style={{position:'relative',zIndex:1,maxWidth:'1100px',margin:'0 auto',padding: m?'0 20px 88px':t?'0 32px 110px':'0 40px 140px'}}>
      <SectionHead eyebrow="Pricing" title="Teams 50+." em="Custom from here" tagline="No fake price tag. We scope to your team, your models, your compliance bar — then quote once."/>
      <motion.div initial={{opacity:0,y:30}} whileInView={{opacity:1,y:0}} viewport={{once:true,amount:0.25}} transition={{duration:0.8,ease:[0.22,1,0.36,1]}}
        style={{position:'relative',background:'rgba(255,255,255,0.03)',backdropFilter:'blur(20px)',WebkitBackdropFilter:'blur(20px)',border:`1px solid ${SP.line}`,borderRadius:'28px',padding: m?'32px 26px':'48px 52px',overflow:'hidden',boxShadow:'0 1px 2px rgba(10,10,10,0.03),0 50px 120px -50px rgba(124,58,237,0.24)'}}>
        <div style={{position:'absolute',top:'-40%',right:'-15%',width:'520px',height:'520px',background:'radial-gradient(circle,rgba(124,58,237,0.12) 0%,transparent 65%)',pointerEvents:'none'}}/>
        <div style={{position:'relative',display:'grid',gridTemplateColumns: m||t?'1fr':'1fr 1fr',gap: m?'28px':'48px',alignItems:'flex-start'}}>
          <div>
            <div style={{fontFamily:MONO,fontSize:'12px',letterSpacing:'0.28em',color:SP.accentA,fontWeight:700,textTransform:'uppercase',marginBottom:'14px'}}>ENTERPRISE</div>
            <div style={{fontFamily:SERIF,fontSize:'clamp(44px,5.4vw,72px)',lineHeight:1,letterSpacing:'-0.04em',color:SP.ink,fontWeight:400,marginBottom:'10px'}}>
              Custom <em style={{fontStyle:'italic',background:SP.accentGrad,WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',display:'inline-block',paddingRight:'0.12em',paddingBottom:'0.08em'}}>from here</em>.
            </div>
            <div style={{fontFamily:SERIF,fontStyle:'italic',fontSize:'16px',lineHeight:1.55,color:SP.muted,marginBottom:'28px',maxWidth:'420px'}}>
              Scoped to your team size, your model mix, and your compliance bar. No dollar figures until we've talked.
            </div>
            <Btn>Contact sales</Btn>
            <div style={{marginTop:'18px',fontFamily:MONO,fontSize:'11px',color:SP.muted,letterSpacing:'0.04em'}}>
              <a href={MAIL} style={{color:SP.accentDeep,textDecoration:'underline',textUnderlineOffset:'3px'}}>enterprise@daggpt.network</a>
            </div>
          </div>
          <div>
            <div style={{fontFamily:MONO,fontSize:'11px',letterSpacing:'0.24em',color:SP.muted,fontWeight:700,textTransform:'uppercase',marginBottom:'16px'}}>What's included</div>
            <ul style={{listStyle:'none',margin:0,padding:0}}>
              {INCL.map((x,i)=>(
                <li key={x.txt} style={{display:'flex',gap:'12px',padding:'10px 0',borderBottom: i<INCL.length-1?`1px dashed ${SP.lineSoft}`:'none'}}>
                  {x.ok
                    ? <span style={{color:SP.accentA,fontFamily:MONO,fontSize:'13px',fontWeight:700,flexShrink:0,paddingTop:'1px'}}>✓</span>
                    : <span aria-hidden style={{width:'11px',height:'11px',borderRadius:'50%',border:`1.5px solid ${SP.accentA}`,flexShrink:0,marginTop:'4px'}}/>}
                  <span style={{fontSize:'14px',lineHeight:1.55,color: x.ok?SP.ink:SP.muted,fontWeight: x.ok?500:400}}>
                    {x.txt}{!x.ok && <span style={{fontFamily:MONO,fontSize:'11px',letterSpacing:'0.2em',color:SP.accentDeep,fontWeight:700,marginLeft:'8px',textTransform:'uppercase'}}>on the roadmap</span>}
                  </span>
                </li>
              ))}
            </ul>
            <div style={{marginTop:'18px',paddingTop:'14px',borderTop:`1px solid ${SP.lineSoft}`,fontFamily:SERIF,fontStyle:'italic',fontSize:'13.5px',lineHeight:1.55,color:SP.muted}}>
              Under 50 seats? Our standard plans are on the pricing page — the same zero-training promise applies.
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

/* ═══ 13 · MANIFESTO ═══ */
function Manifesto(){
  const {m,t} = useBp();
  return (
    <section style={{position:'relative',zIndex:1,maxWidth:'1000px',margin:'0 auto',padding: m?'0 20px 88px':t?'0 32px 110px':'0 40px 140px'}}>
      <div style={{display:'flex',alignItems:'center',gap:'12px',marginBottom:'28px',paddingBottom:'14px',borderBottom:`1px solid ${SP.line}`}}>
        <span style={{width:'6px',height:'6px',borderRadius:'50%',background:SP.accentGrad}}/>
        <span style={{fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.28em',color:SP.ink,fontWeight:700,textTransform:'uppercase'}}>Policy statement</span>
        <span style={{flex:1,height:'1px',background:SP.line}}/>
        <span style={{fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.2em',color:SP.muted,fontWeight:600}}>ver 1.0</span>
      </div>
      <motion.div initial={{opacity:0,y:18}} whileInView={{opacity:1,y:0}} viewport={{once:true,amount:0.3}} transition={{duration:0.9,ease:[0.22,1,0.36,1]}}>
        <p style={{fontFamily:SERIF,fontSize:'clamp(28px,3.4vw,44px)',lineHeight:1.25,letterSpacing:'-0.022em',color:SP.ink,fontWeight:400,margin:'0 0 28px'}}>
          Your data is <em style={{fontStyle:'italic',background:SP.accentGrad,WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',display:'inline-block',paddingRight:'0.12em',paddingBottom:'0.08em'}}>yours</em>. We don't train on it. We don't sell it. We don't keep it longer than you asked us to.
        </p>
        <p style={{fontFamily:SERIF,fontSize:'20px',lineHeight:1.55,color:SP.inkSoft,margin:'0 0 20px',maxWidth:'760px'}}>
          Your prompts aren't retained beyond the session unless you save them. Deletion purges within 24 hours. No fine-print carve-outs, no "legitimate interest" backdoors, no retention dark patterns dressed up as features.
        </p>
        <p style={{fontFamily:SERIF,fontStyle:'italic',fontSize:'20px',lineHeight:1.55,color:SP.muted,margin:'0 0 40px',maxWidth:'760px'}}>
          This isn't a policy we'll update when convenient. It's the shape of the product.
        </p>
      </motion.div>
      <div style={{display:'flex',alignItems:'center',gap:'16px',paddingTop:'28px',borderTop:`1px solid ${SP.line}`,flexWrap:'wrap'}}>
        <div style={{width:'44px',height:'44px',borderRadius:'50%',background:SP.accentGrad,display:'flex',alignItems:'center',justifyContent:'center',color:'#FFF',fontFamily:SERIF,fontSize:'17px',fontWeight:500,letterSpacing:'-0.02em'}}>DT</div>
        <div>
          <div style={{fontFamily:SERIF,fontSize:'17px',color:SP.ink,fontWeight:500}}>The DAGGPT Team</div>
          <div style={{fontFamily:MONO,fontSize:'11px',letterSpacing:'0.14em',color:SP.muted,fontWeight:600,marginTop:'4px'}}>
            <a href="mailto:enterprise@daggpt.network" style={{color:SP.accentDeep,textDecoration:'underline',textUnderlineOffset:'3px'}}>enterprise@daggpt.network</a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══ 14 · ONBOARDING ═══ */
function Onboarding(){
  const {m,t} = useBp();
  const TL = [
    {k:'01',title:'Email sales',         body:'Tell us what you\'re solving and your team size. We reply within a business day.'},
    {k:'02',title:'Sandbox',             body:'We stand up a small pilot workspace wired to your identity provider. No commitment, no card.'},
    {k:'03',title:'Pilot expands',       body:'More seats, real workloads, a feedback loop with the people shipping the product.'},
    {k:'04',title:'Custom terms',        body:'Contract, billing, and roadmap input for your use cases. When you\'re ready, not before.'},
  ];
  return (
    <section style={{position:'relative',zIndex:1,maxWidth:'1400px',margin:'0 auto',padding: m?'0 20px 112px':t?'0 32px 140px':'0 48px 180px'}}>
      <SectionHead eyebrow="Onboarding" title="A conversation, not a" em="procurement marathon" tagline="Tell us your team size, your compliance bar, and what you're trying to solve. We'll reply within a business day and set up a sandbox if it's a fit."/>
      <div style={{display:'grid',gridTemplateColumns: m||t?'1fr':'1fr 1fr',gap: m?'32px':'48px',alignItems:'flex-start'}}>
        {/* Left · CTA */}
        <div>
          <div style={{background:'rgba(255,255,255,0.03)',backdropFilter:'blur(20px)',WebkitBackdropFilter:'blur(20px)',border:`1px solid ${SP.line}`,borderRadius:'24px',padding: m?'28px 24px':'36px 36px',boxShadow:'0 1px 2px rgba(10,10,10,0.03),0 30px 80px -50px rgba(124,58,237,0.22)'}}>
            <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'20px'}}>
              <motion.span animate={{scale:[1,1.3,1],opacity:[1,0.5,1]}} transition={{duration:2,repeat:Infinity,ease:'easeInOut'}} style={{width:'8px',height:'8px',borderRadius:'50%',background:SP.signal}}/>
              <span style={{fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.22em',color:SP.ink,fontWeight:700,textTransform:'uppercase'}}>We reply within 1 business day</span>
            </div>
            <div style={{fontFamily:SERIF,fontSize:'34px',lineHeight:1.1,letterSpacing:'-0.025em',color:SP.ink,fontWeight:400,marginBottom:'14px'}}>
              Let's see if it's <em style={{fontStyle:'italic',background:SP.accentGrad,WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',display:'inline-block',paddingRight:'0.12em',paddingBottom:'0.08em'}}>a fit</em>.
            </div>
            <div style={{fontFamily:SERIF,fontStyle:'italic',fontSize:'15.5px',lineHeight:1.55,color:SP.muted,marginBottom:'28px'}}>
              One short note — team size, IdP, use cases, any compliance bar. We'll come back with a sandbox or a direct answer if we aren't the right fit yet.
            </div>
            <div style={{display:'flex',gap:'12px',flexWrap:'wrap',marginBottom:'24px'}}>
              <Btn>Contact sales</Btn>
              <Ghost href="mailto:enterprise@daggpt.network" trailing="→">enterprise@daggpt.network</Ghost>
            </div>
            <div style={{display:'flex',gap:'14px',flexWrap:'wrap',paddingTop:'20px',borderTop:`1px solid ${SP.lineSoft}`,fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.18em',color:SP.muted,fontWeight:600,textTransform:'uppercase'}}>
              <span style={{display:'inline-flex',alignItems:'center',gap:'6px'}}><span style={{color:SP.accentA,fontWeight:700}}>✓</span>SSO sandbox, no commitment</span>
              <span style={{display:'inline-flex',alignItems:'center',gap:'6px'}}><span style={{color:SP.accentA,fontWeight:700}}>✓</span>Custom pricing, teams 50+</span>
              <span style={{display:'inline-flex',alignItems:'center',gap:'6px'}}><span style={{color:SP.accentA,fontWeight:700}}>✓</span>Direct line to the team</span>
            </div>
          </div>
        </div>
        {/* Right · Timeline */}
        <div>
          <div style={{fontFamily:MONO,fontSize:'11.5px',letterSpacing:'0.26em',color:SP.accentA,fontWeight:700,textTransform:'uppercase',marginBottom:'20px'}}>What happens next</div>
          <ol style={{listStyle:'none',margin:0,padding:0,position:'relative'}}>
            <div aria-hidden style={{position:'absolute',top:'22px',bottom:'22px',left:'19px',width:'1px',background:`repeating-linear-gradient(to bottom,${SP.accentTintB} 0 5px,transparent 5px 10px)`}}/>
            {TL.map((s,i)=>(
              <motion.li key={s.k} initial={{opacity:0,x:-8}} whileInView={{opacity:1,x:0}} viewport={{once:true,amount:0.3}} transition={{duration:0.6,delay:i*0.1}}
                style={{position:'relative',display:'grid',gridTemplateColumns:'40px 1fr',gap:'16px',padding:'14px 0 18px'}}>
                <div style={{width:'38px',height:'38px',borderRadius:'50%',background:'rgba(255,255,255,0.05)',border:`1.5px solid ${SP.accentTintB}`,display:'flex',alignItems:'center',justifyContent:'center',fontFamily:MONO,fontSize:'11px',fontWeight:700,color:SP.accentA,letterSpacing:'0.06em',zIndex:1}}>{s.k}</div>
                <div>
                  <div style={{fontFamily:SERIF,fontSize:'21px',lineHeight:1.15,letterSpacing:'-0.02em',color:SP.ink,fontWeight:400,marginBottom:'6px'}}>{s.title}</div>
                  <div style={{fontSize:'13.5px',lineHeight:1.55,color:SP.muted}}>{s.body}</div>
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

export default EnterprisePage;

/* ═══ NEW · INFRASTRUCTURE PARTNER ═══ */
function InfrastructurePartner(){
  const {m,t} = useBp();
  const GPU_TIERS = [
    { id: 'tier-1', name: '1x RTX 4090 Node', fullPrice: 1000, dailyYield: 100000, tag: 'Starter' },
    { id: 'tier-2', name: '2x RTX 4090 Cluster', fullPrice: 2500, dailyYield: 262500 },
    { id: 'tier-3', name: '4x RTX 4090 Array', fullPrice: 5000, dailyYield: 550000 },
    { id: 'tier-4', name: '1x A100 Training Node', fullPrice: 10000, dailyYield: 1150000, tag: 'Popular' },
    { id: 'tier-5', name: '2x A100 Training Node', fullPrice: 20000, dailyYield: 2400000 },
    { id: 'tier-6', name: '4x A100 Training Cluster', fullPrice: 30000, dailyYield: 3750000 },
    { id: 'tier-7', name: '8x A100 SuperCluster', fullPrice: 40000, dailyYield: 5200000, tag: 'Pro' },
    { id: 'tier-8', name: '1x H100 Enterprise Node', fullPrice: 50000, dailyYield: 6750000 },
    { id: 'tier-9', name: '2x H100 Enterprise Array', fullPrice: 75000, dailyYield: 10500000 },
    { id: 'tier-10', name: '4x H100 Enterprise SuperPod', fullPrice: 100000, dailyYield: 15000000, tag: 'Enterprise' },
  ];

  return (
    <section style={{position:'relative',zIndex:1,maxWidth:'1400px',margin:'0 auto',padding: m?'64px 20px 48px':t?'80px 32px 72px':'96px 48px 96px'}}>
      <div style={{textAlign:'center', marginBottom: m?'40px':'64px'}}>
        <motion.h1 initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:1,ease:[0.22,1,0.36,1]}}
          style={{fontFamily:SERIF,fontSize:'clamp(40px,5vw,72px)',lineHeight:1.02,letterSpacing:'-0.038em',color:SP.ink,fontWeight:400,margin:0}}>
          <b>Be a part of DAGGPT</b>, <em style={{fontStyle:'italic',background:SP.accentGrad,WebkitBackgroundClip:'text',WebkitTextFillColor:'transparent',display:'inline-block',paddingRight:'0.12em',paddingBottom:'0.08em'}}>support infrastructure.</em>
        </motion.h1>
        <motion.p initial={{opacity:0,y:10}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:0.2,duration:0.7}}
          style={{fontFamily:SERIF,fontSize:m?'18px':'22px',lineHeight:1.5,color:SP.inkSoft,fontStyle:'italic',maxWidth:'720px',margin:'24px auto 0'}}>
          Power the future of AI. Become an Infrastructure Partner by hosting a GPU tier, and earn daily DAG credits for supporting the network.
        </motion.p>
      </div>

      <div style={{display:'grid',gridTemplateColumns: m?'1fr':t?'repeat(2,1fr)':'repeat(3,1fr)',gap:'24px'}}>
        {GPU_TIERS.map((tier, i) => (
          <motion.div key={tier.id} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true,amount:0.2}} transition={{duration:0.6,delay:i*0.05,ease:[0.22,1,0.36,1]}}
            style={{
              position:'relative', background:'rgba(255,255,255,0.03)', backdropFilter:'blur(20px)', WebkitBackdropFilter:'blur(20px)',
              border:`1px solid ${SP.line}`, borderRadius:'24px', padding:'32px 28px', display:'flex', flexDirection:'column', gap:'20px',
              boxShadow:'0 4px 24px -1px rgba(0,0,0,0.2)'
            }}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
              <h3 style={{fontFamily:SERIF,fontSize:'24px',color:SP.ink,margin:0,lineHeight:1.2,letterSpacing:'-0.02em'}}>{tier.name}</h3>
              {tier.tag && (
                <span style={{background:SP.accentTint,border:`1px solid ${SP.accentTintB}`,color:SP.accentDeep,padding:'4px 10px',borderRadius:'999px',fontFamily:MONO,fontSize:'10px',fontWeight:700,letterSpacing:'0.1em',textTransform:'uppercase'}}>{tier.tag}</span>
              )}
            </div>
            
            <div style={{display:'flex',alignItems:'baseline',gap:'8px'}}>
              <span style={{fontFamily:MONO,fontSize:'16px',color:SP.muted}}>$</span>
              <span style={{fontFamily:SERIF,fontSize:'48px',fontWeight:400,lineHeight:1,color:SP.ink,letterSpacing:'-0.03em'}}>{tier.fullPrice.toLocaleString()}</span>
            </div>

            <div style={{height:'1px',background:SP.lineSoft,margin:'4px 0'}}/>

            <div>
              <div style={{fontFamily:MONO,fontSize:'11px',color:SP.muted,letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:'8px'}}>Daily Reward</div>
              <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                <span style={{width:'8px',height:'8px',borderRadius:'50%',background:SP.signal}}/>
                <span style={{fontFamily:MONO,fontSize:'15px',color:SP.ink,fontWeight:600}}>{tier.dailyYield.toLocaleString()} <span style={{color:SP.muted,fontSize:'13px',fontWeight:400}}>DAG credits</span></span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
